{
// Pure Purchase Modal calculations. Inputs are numeric product/configuration data only.
// Kept with the controller to avoid an extra asset or shared-theme dependency.
const PurchaseModalPricing = {
  calculateTotals(state) {
    if (state.experience === "buy-x-pay-y" && !state.compositionLxpy) {
      const variant = state.steps[0]?.variant || PurchaseModalPricing.lowestEligibleVariant(state.steps[0]?.product, state.steps[0]?.selectedOptions);

      if (!variant) {
        return {
          pristinePrice: 0,
          compareAtPrice: 0,
          discountedPrice: 0,
          discountPercentage: 0,
        };
      }

      if (state.selectedTier?.type === "unit") {
        const price = Number(variant.price || 0);
        const compareAtPrice = Math.max(price, Number(variant.compare_at_price || 0));

        return {
          pristinePrice: price,
          compareAtPrice,
          discountedPrice: price,
          discountPercentage: 0,
        };
      }
    }

    let pristinePrice = 0;
    let compareAtPrice = 0;
    let discountedPrice = 0;
    const rule = state.selectedTier?.rule || state.config.rules?.[0] || {};
    const ruleDiscount = Number(rule.discountPercentage || 0);

    state.steps.forEach((step) => {
      const variant = step.variant || PurchaseModalPricing.lowestEligibleVariant(step.product, step.selectedOptions);
      if (!variant) return;

      const quantity =
        state.experience === "progressive-discount"
          ? Number(rule.quantityRequiredForDiscount || 1)
          : state.experience === "buy-x-pay-y" && !state.compositionLxpy
            ? (state.selectedTier?.type === 'unit' ? 1 : PurchaseModalPricing.getBuyXPayYQuantity(state.selectedTier?.config || state.config))
            : Number(step.quantity || 1);
      const price = Number(variant.price || 0);
      const compare = Math.max(price, Number(variant.compare_at_price || 0));
      let itemDiscount = ruleDiscount;

      if (state.config.relativeDiscountWeight && step.composition?.discountPercentageWeight != null) {
        itemDiscount = ruleDiscount * (Number(step.composition.discountPercentageWeight || 0) / 100);
      }

      pristinePrice += price * quantity;
      compareAtPrice += compare * quantity;
      discountedPrice += price * quantity * (1 - itemDiscount / 100);
    });

    if (state.experience === "buy-x-pay-y" && !state.compositionLxpy && state.steps[0]) {
      const variant = state.steps[0].variant || PurchaseModalPricing.lowestEligibleVariant(state.steps[0].product, state.steps[0].selectedOptions);
      const config = state.selectedTier?.config || state.config;
      const price = Number(variant?.price || 0);
      const compare = Math.max(price, Number(variant?.compare_at_price || 0));
      const composition = config.productComposition || [];
      const totalQuantity = PurchaseModalPricing.getBuyXPayYQuantity(config);
      let discountValue = 0;

      composition.forEach((item) => {
        const quantity = Number(item.quantity || 0);
        const weight = Number(item.discountPercentageWeight || 0) / 100;
        discountValue += price * quantity * weight * (ruleDiscount / 100);
      });

      pristinePrice = price * totalQuantity;
      compareAtPrice = compare * totalQuantity;
      discountedPrice = Math.max(0, pristinePrice - discountValue);
    }

    const discountPercentage = pristinePrice > 0 ? ((pristinePrice - discountedPrice) * 100) / pristinePrice : 0;

    return { pristinePrice, compareAtPrice, discountedPrice, discountPercentage };

  },
  getBuyXPayYQuantity(config) {
    const compositionQuantity = (config.productComposition || []).reduce(
      (total, item) => total + Number(item.quantity || 0),
      0,
    );

    if (compositionQuantity > 0) {
      return compositionQuantity;
    }

    return Math.max(1, Number(config.rules?.[0]?.quantityRequiredForDiscount || 1));

  },
  roundDisplayedPrice(cents) {
    // Same whole-BRL display rounding as price-normalize; never changes commercial totals.
    const normalized = Math.round(Number(cents) * 1000000) / 1000000;
    return Math.ceil(normalized / 100) * 100;
  },
  displayedDiscount(reference, final) {
    const referenceValue = this.roundDisplayedPrice(reference);
    const finalValue = this.roundDisplayedPrice(final);
    if (!Number.isFinite(referenceValue) || !Number.isFinite(finalValue) ||
        referenceValue <= 0 || finalValue < 0 || finalValue >= referenceValue) return 0;

    const percentage = ((referenceValue - finalValue) / referenceValue) * 100;
    return Math.floor(Math.round(percentage * 1000000) / 1000000);
  },
  getBuyXPayYTierPrice(state, tier) {
    const config = tier.type === 'promotion' ? tier.config : state.config;
    const step = state.steps[0];
    const variant = step?.variant || this.lowestEligibleVariant(step?.product, step?.selectedOptions);
    if (!config || !variant) return null;

    const totals = this.calculateTotals({
      ...state,
      config,
      selectedTier: { ...tier, config, rule: tier.rule || config.rules?.[0] || {} },
      steps: [{ ...step, variant }],
    });
    const quantity = tier.type === 'unit' ? 1 : this.getBuyXPayYQuantity(config);
    return {
      unitPrice: totals.discountedPrice / quantity,
      compareAtUnitPrice: totals.compareAtPrice / quantity,
      totalPrice: totals.discountedPrice,
      compareAtPrice: totals.compareAtPrice,
      quantity,
    };
  },
  getBuyXPayYDisplayedDiscount(price) {
    return price ? this.displayedDiscount(
      Number(price.compareAtPrice ?? price.compareAtUnitPrice ?? 0),
      Number(price.totalPrice ?? price.unitPrice ?? 0),
    ) : 0;
  },
  getBuyXPayYEffectiveDiscount(config) {
    const composition = config?.productComposition || [];

    const ruleDiscount = Math.max(0, Math.min(100, Number(config?.rules?.[0]?.discountPercentage || 0)));

    const totalQuantity = composition.reduce((total, item) => total + Math.max(0, Number(item.quantity || 0)), 0);

    if (!totalQuantity) {
      return ruleDiscount;
    }

    const discountedQuantity = composition.reduce((total, item) => {
      const quantity = Math.max(0, Number(item.quantity || 0));

      const weight = Math.max(0, Math.min(100, Number(item.discountPercentageWeight || 0)));

      const effectiveItemDiscount = ruleDiscount * (weight / 100);

      return total + quantity * (effectiveItemDiscount / 100);
    }, 0);

    return (discountedQuantity / totalQuantity) * 100;

  },
  lowestEligibleVariant(product, selectedOptions = []) {
    return (product?.variants || []).filter((variant) => !variant.hidden && variant.available &&
      selectedOptions.every((value, index) => value == null || variant.options[index] === value))
      .reduce((lowest, variant) => !lowest || Number(variant.price) < Number(lowest.price) ? variant : lowest, null);

  },
  firstAvailableVariant(product) {
    return product?.variants.find((variant) => !variant.hidden && variant.available) || null;

  },
  findVariant(product, selectedOptions) {
    if (selectedOptions.some((value) => value === null)) return null;

    return (
      product.variants.find(
        (variant) =>
          !variant.hidden &&
          variant.available &&
          variant.options.every((value, index) => value === selectedOptions[index]),
      ) || null
    );

  },
  result(state, { header = false } = {}) {
    let totals = this.calculateTotals(state);
    const single = state.experience === 'single-product';
    const unitHeader = header && state.experience === 'single-product';
    if (unitHeader) {
      const step = state.steps[0];
      const variant = step?.variant || (single
        ? this.lowestEligibleVariant(step?.product, step?.selectedOptions)
        : this.firstAvailableVariant(step?.product));
      if (!variant) return null;
      const price = Number(variant.price || 0);
      totals = { pristinePrice: price, compareAtPrice: Math.max(price, Number(variant.compare_at_price || 0)), discountedPrice: price, discountPercentage: 0 };
    }
    const isKit = state.compositionLxpy || ['mono-product-kit', 'multi-product-kit', 'buy-together', 'buy-and-save'].includes(state.experience);
    const varies = state.steps.some(step => new Set((step.product?.variants || [])
      .filter(v => !v.hidden && v.available && (single || (state.experience === 'buy-x-pay-y' && !state.compositionLxpy && !state.hasUserKitSelection) || (step.selectedOptions || []).every((value, index) => value == null || v.options[index] === value)))
      .map(v => Number(v.price))).size > 1);
    return {
      beforeDiscount: totals.pristinePrice,
      referenceValue: totals.compareAtPrice,
      offerDiscount: totals.pristinePrice - totals.discountedPrice,
      offerDiscountPercentage: totals.discountPercentage,
      finalValue: totals.discountedPrice, // Before coupon/PIX and display rounding.
      selection: state.selectionComplete ? 'complete' : 'partial',
      showFrom: single ? !state.hasUserKitSelection && varies
        : unitHeader ? false : isKit ? !state.hasUserKitSelection : !state.selectionComplete && varies,
      unitHeader,
    };
  },
  stepDiscount(step, config, rule) {
    const discount = Number(rule.discountPercentage || 0);
    return config.relativeDiscountWeight && step.composition?.discountPercentageWeight != null
      ? discount * (Number(step.composition.discountPercentageWeight || 0) / 100) : discount;
  },
};

// Modal-only boundary: the shared component retains coupon/PIX calculation and rendering.
const PurchaseModalPriceAdapter = {
  attributes(result) {
    return {
      pristinePrice: String(result.beforeDiscount), minPrice: String(result.beforeDiscount),
      compareAtPrice: String(result.referenceValue), kitDiscount: String(result.offerDiscountPercentage),
      priceVaries: String(result.showFrom), ...(result.unitHeader ? {} : { kitProducts: 'false' }),
    };
  },
  badgeValues(element, presentation) {
    if (!element || typeof element.calculatePrice !== 'function') return null;
    const data = element.dataset;
    // Eligibility/type and coupon rate are already resolved by price-normalize.
    const kit = data.kitProducts === 'true';
    const base = kit ? Number(data.kitFinalPrice || 0)
      : element.calculatePrice(Number(data.pristinePrice || 0), Math.abs(Number(data.kitDiscount || 0)));
    let finalValue = base;
    if (presentation === 'coupon' || (presentation === 'pix' && data.type === 'PixAsOriginalPrice')) {
      const coupon = data.finalPriceApplied === 'true' && !kit ? 0 : Number(data.priceObjectDiscountValue || 0);
      finalValue = element.calculatePrice(finalValue, coupon);
    }
    if (presentation === 'pix') finalValue = element.calculatePrice(finalValue, Math.abs(Number(data.pixDiscountValue || 0)));
    // Match the shared display boundary (upward whole BRL), without reading formatted text.
    return {
      referenceValue: PurchaseModalPricing.roundDisplayedPrice(Number(data.compareAtPrice || base)),
      finalValue: PurchaseModalPricing.roundDisplayedPrice(finalValue),
    };
  },
  presentation(element) {
    return element?.querySelector(".coupon-price") ? 'coupon'
      : element?.querySelector(".pix-price > .init-price") ? 'pix'
      : element?.querySelector(".original-price") ? 'original' : null;
  },
  singlePackValues(pack) {
    const presentation = this.presentation(pack);
    const base = Number(pack?.dataset.pristinePrice);
    const reference = Number(pack?.dataset.compareAtPrice);
    if (!presentation || !Number.isFinite(base) || base <= 0 ||
        !Number.isFinite(reference) || reference <= 0) return null;
    // Keep the composition's catalog reference, as in the card, PDK and cart drawer.
    const values = this.badgeValues(pack, presentation);
    if (!values || !Number.isFinite(values.finalValue)) return null;
    return { ...values, discount: PurchaseModalPricing.displayedDiscount(values.referenceValue, values.finalValue) };
  },
  apply(element, result, timestamp) {
    Object.assign(element.dataset, this.attributes(result));
    element.dataset.timestamp = timestamp;
  },
};



// Internal facts for this operation only. No tracking SDK, user attribution or external event.
const PurchaseModalIntegration = {
  context(experience, operationId, redirectToCheckout) {
    const product = experience.products[0];
    const unit = experience.experience === "single-product" ||
      (experience.experience === "buy-x-pay-y" && experience.selectedTier?.type === "unit");
    const promotion = unit ? null : experience.selectedTier?.config || experience.config;
    return Object.freeze({
      origin: experience.integrationEntry?.origin || experience.dataset.requestedTrackingContext || "pdp",
      placement: experience.integrationEntry?.placement || "",
      product: Object.freeze({ id: String(experience.dataset.productId || product?.id || ""), handle: product?.handle || "" }),
      promotion: promotion ? Object.freeze({ id: String(promotion.id), source: experience.resolveProductSource(promotion) }) : null,
      experience: experience.experience,
      operation: Object.freeze({ id: operationId, type: redirectToCheckout ? "checkout" : "add-to-cart" }),
    });
  },
  record(operation, type, stage = "cart", details = {}) {
    if (!operation?.facts) return;
    // A feedback failure must never be classified as a failed cart POST.
    if (stage === "cart" && type === "error" && operation.outcome === "success") return;
    if (operation.facts.some(fact => fact.type === type && fact.stage === stage)) return;
    operation.facts.push(Object.freeze({ type, stage, context: operation.context, ...details }));
  },
};

if (!customElements.get("purchase-modal")) {
  customElements.define(
    "purchase-modal",
    class PurchaseModal extends HTMLElement {
      constructor() {
        super();
        this.cache = new Map();
        this.cacheState = { entries: this.cache, revision: 0 };
        this.cacheTTL = 5 * 60 * 1000;
        this.cacheLimit = 20;
        this.abortController = null;
        this.activeTrigger = null;
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDialogClick = this.handleDialogClick.bind(this);
        this.handleDialogCancel = this.handleDialogCancel.bind(this);
        this.handleSuccess = this.handleSuccess.bind(this);
        this.openRequestId = 0;
        this.openLookFromQuery = this.openLookFromQuery.bind(this);
      }

      connectedCallback() {
        if (this.connectionController && !this.connectionController.signal.aborted) return;
        this.connectionController = new AbortController();
        const options = { signal: this.connectionController.signal }; 
        this.dialog = this.querySelector("[data-pam-dialog]");
        this.content = this.querySelector("[data-pam-content]");
        this.loading = this.querySelector("[data-pam-loading]");
        this.requestError = this.querySelector("[data-pam-request-error]");
        this.closeButton = this.querySelector("[data-pam-close]");
        this.defaultDialogLabel = this.dialog?.getAttribute("aria-label") || "";
        this.errorLabel = this.dataset.errorLabel || "Indisponível";

        if (!this.dialog || !this.content) {
          this.connectionController.abort();
          return;
        }

        document.addEventListener("click", this.handleDocumentClick, options);
        this.dialog.addEventListener("click", this.handleDialogClick, options);
        this.dialog.addEventListener("cancel", this.handleDialogCancel, options);
        this.addEventListener("purchase-modal:success", this.handleSuccess, options);
        this.closeButton?.addEventListener("click", () => this.close(), options);
        this.openFromQuery();
        document.addEventListener("shopify:section:load", this.openLookFromQuery, options);
      }

      disconnectedCallback() {
        this.connectionController?.abort();
        this.connectionController = null;
        this.close();
        this.invalidateCache();
        for (const key of ["dialog", "content", "loading", "requestError", "closeButton"]) this[key] = null;
      }

      teardownExperience() {
        this.openRequestId += 1;
        this.abortController?.abort();
        this.abortController = null;
        cancelAnimationFrame(this.openScrollFrame);
        this.openScrollFrame = null;
        this.activeTrigger?.removeAttribute("aria-disabled");
        this.content?.querySelector("[data-pam-experience]")?.destroy?.();
        this.content?.replaceChildren();
        if (this.requestError) {
          this.requestError.hidden = true;
          this.requestError.textContent = "";
        }
        this.setLoading(false);
      }

      openFromQuery() {
        const params = new URLSearchParams(window.location.search);
        const handle = String(params.get("open-product") || "")
          .trim()
          .toLowerCase();

        if (!handle) {
          this.openLookFromQuery();
          return;
        }

        // Garante que a query só possa representar handles Shopify.
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) return;

        params.delete("open-product");

        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`;

        window.history.replaceState({}, "", nextUrl);

        const rootUrl = String(window.routes?.root_url || "/").replace(/\/?$/, "/");
        const productUrl = new URL(`${rootUrl}products/${handle}`, window.location.origin);

        this.open(productUrl.toString(), "purchaseModal", null);
      }

      openLookFromQuery() {
        const url = new URL(window.location.href);
        const triggers = [...document.querySelectorAll("[data-pam-look-entry][data-purchase-modal-trigger]")];
        const trigger = triggers.find((candidate) => {
          const key = candidate.dataset.pamLookQuery || "look";
          if (!url.searchParams.has(key) || candidate.disabled || candidate.getAttribute("aria-disabled") === "true") return false;
          const value = url.searchParams.get(key).trim();
          return ["", "1", "true", "open"].includes(value.toLowerCase()) ||
            value === candidate.dataset.promotionId || value === candidate.dataset.pamLookAlias;
        });
        if (!trigger) return;
        url.searchParams.delete(trigger.dataset.pamLookQuery || "look");
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
        this.open(trigger.dataset.productUrl, "purchaseBuyTogether", trigger);
      }

      handleDocumentClick(event) {
        const trigger = event.target.closest("[data-purchase-modal-trigger]");

        if (!trigger) return;

        if (trigger.hasAttribute("aria-disabled")) {
          event.preventDefault();
          return;
        }

        const productUrl = trigger.dataset.productUrl || trigger.getAttribute("href");

        const view = trigger.dataset.purchaseView;

        if (!productUrl || !view) return;

        event.preventDefault();

        this.open(productUrl, view, trigger);
      }

      handleDialogClick(event) {
        const packTrigger = event.target.closest("[data-pam-single-pack]");

        if (packTrigger) {
          event.preventDefault();

          if (packTrigger.getAttribute("aria-pressed") === "true") return;

          const productUrl = packTrigger.dataset.productUrl;
          const view = packTrigger.dataset.purchaseView || "purchaseModal";

          if (!productUrl) return;

          // Mantém o trigger externo original para receber foco no fechamento.
          this.open(productUrl, view, packTrigger);
          return;
        }

        if (event.target === this.dialog) this.close();
      }

      handleDialogCancel(event) {
        event.preventDefault();
        this.close();
      }

      async handleSuccess(event) {
        // Eventos de uma experiência substituída nunca afetam a abertura atual.
        if (event.target && event.target !== this.content?.querySelector("[data-pam-experience]")) return;
        const operation = event.target?.operation;
        if (operation?.feedbackHandled) return;
        if (operation) operation.feedbackHandled = true;
        try {
          if (event.detail?.redirectToCheckout) {
            this.close();

            const rootUrl = String(window.routes?.root_url || "/").replace(/\/?$/, "/");
            window.location.assign(`${rootUrl}checkout`);
            return;
          }

          const cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
          const response = event.detail?.response;

          this.close();

          if (typeof window.CartFeedback?.present === "function") {
            await window.CartFeedback.present(cart, response, "purchase-modal");
          } else if (typeof cart?.renderContents === "function" && response?.sections) {
            await cart.renderContents(response, false);
          }
        } catch (error) {
          // O POST já foi confirmado: falha de apresentação não é falha de compra.
          PurchaseModalIntegration.record(operation, "error", "feedback");
          console.error("Purchase modal: falha no feedback após adição confirmada", error);
        }
      }

      async open(productUrl, view, trigger) {
        // Um botão de kit interno não deve substituir o destino de foco externo.
        const internalTrigger = Boolean(trigger && this.content?.contains?.(trigger));
        const entry = internalTrigger ? this.integrationEntry : Object.freeze({
          origin: trigger?.dataset?.insContext || "pdp",
          placement: trigger?.dataset?.placement || trigger?.dataset?.insElementPosition || "",
        });
        const focusTrigger = internalTrigger ? this.activeTrigger : trigger?.closest?.("[data-buy-together-external-menu]")
          ?.querySelector("[data-buy-together-menu-trigger]") || trigger;
        this.teardownExperience();
        const requestId = this.openRequestId;
        this.activeTrigger = focusTrigger;
        this.integrationEntry = entry;
        const context = {
          promotionId: trigger?.dataset?.promotionId || "",
          productSource: trigger?.dataset?.productSource || "",
        };

        this.dialog.removeAttribute("aria-labelledby");
        this.dialog.setAttribute("aria-label", this.defaultDialogLabel);

        this.setLoading(true);

        if (!this.dialog.open) {
          this.dialog.showModal();
        }

        document.documentElement.classList.add("pam-is-open");

        try {
          const html = await this.loadProduct(productUrl, view, context);

          /*
           * Outra abertura aconteceu enquanto aguardávamos.
           * Esta resposta não deve mais alterar o modal.
           */
          if (requestId !== this.openRequestId) return;

          this.content.innerHTML = html;

          const experience = this.content.querySelector("[data-pam-experience]");

          if (!experience) {
            throw new Error(this.errorLabel);
          }

          experience.dataset.requestedPromotionId = trigger?.dataset?.promotionId || "";

          experience.dataset.requestedProductSource = trigger?.dataset?.productSource || "";
          experience.dataset.requestedTrackingContext = trigger?.dataset?.insContext || "pdp";

          experience.integrationEntry = entry;
          experience.cacheState = this.cacheState;
          experience.initialize?.();

          this.content.scrollTop = 0;

          this.openScrollFrame = requestAnimationFrame(() => {
            if (requestId !== this.openRequestId) return;

            experience.querySelector("[data-pam-body]")?.scrollTo({
              top: 0,
              behavior: "instant",
            });
          });

          const title = experience.querySelector(".pam-product__title");

          if (title?.id) {
            this.dialog.setAttribute("aria-labelledby", title.id);

            this.dialog.removeAttribute("aria-label");
          }
        } catch (error) {
          if (error.name === "AbortError") return;
          if (requestId !== this.openRequestId) return;

          // Não conservar fragmento que falhou ao inicializar.
          this.invalidateCache();
          this.teardownExperience();
          this.requestError.textContent = error.message || this.errorLabel;

          this.requestError.hidden = false;
        } finally {
          /*
           * Uma abertura antiga nunca pode desligar o loading
           * da abertura mais recente.
           */
          if (requestId === this.openRequestId) {
            this.setLoading(false);
          }
        }
      }

      close() {
        this.teardownExperience();
        if (this.dialog?.open) this.dialog.close();

        document.documentElement.classList.remove("pam-is-open");

        if (this.activeTrigger?.isConnected) {
          this.activeTrigger.removeAttribute("aria-disabled");
          this.activeTrigger.focus();
        }

        this.activeTrigger = null;
        this.integrationEntry = null;
      }

      setLoading(isLoading) {
        if (this.loading) this.loading.hidden = !isLoading;
        this.dialog?.toggleAttribute("aria-busy", isLoading);
        this.activeTrigger?.toggleAttribute("aria-disabled", isLoading);
      }

      buildEndpoint(productUrl, view) {
        const endpoint = new URL(productUrl, window.location.origin);
        endpoint.hash = "";
        endpoint.searchParams.set("view", view);
        endpoint.searchParams.sort();
        return endpoint.toString();
      }

      buildCacheKey(endpoint, context = {}) {
        // Não agrega produtos/contextos que podem produzir HTML ou preços distintos.
        return JSON.stringify([
          endpoint, context.promotionId || "", context.productSource || "",
          window.Shopify?.locale || document.documentElement.lang || "",
          window.Shopify?.country || "", window.Shopify?.currency?.active || "",
        ]);
      }

      invalidateCache() {
        this.cacheState.entries.clear();
        this.cacheState.revision += 1;
      }

      validateFragment(html, endpoint) {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const roots = parsed.body.children;
        const root = roots[0];
        if (roots.length !== 1 || !root.matches("purchase-experience[data-pam-experience]")) throw new Error(this.errorLabel);
        const views = {
          purchaseSingleProduct: "single-product", purchaseMonoKit: "mono-product-kit",
          purchaseMultiKit: "multi-product-kit", purchaseBuyTogether: "buy-together",
          purchaseBuyXPayY: "buy-x-pay-y", purchaseProgressive: "progressive-discount",
          purchaseBuyAndSave: "buy-and-save",
        };
        const url = new URL(endpoint);
        const view = url.searchParams.get("view");
        if (!Object.values(views).includes(root.dataset.experience) ||
            (view !== "purchaseModal" && views[view] !== root.dataset.experience) ||
            !root.dataset.productId || !root.dataset.productUrl ||
            new URL(root.dataset.productUrl, url.origin).pathname !== url.pathname) throw new Error(this.errorLabel);
        for (const selector of ["[data-pam-body]", "[data-pam-submit]", "[data-pam-checkout]"]) {
          if (!root.querySelector(selector)) throw new Error(this.errorLabel);
        }
        const read = selector => {
          const nodes = root.querySelectorAll(selector);
          if (nodes.length !== 1) throw new Error(this.errorLabel);
          return JSON.parse(nodes[0].textContent);
        };
        try {
          const configs = read("[data-pam-configs]");
          const products = read("[data-pam-products]");
          if (!Array.isArray(configs) || !Array.isArray(products) ||
              configs.some(config => !config || typeof config !== "object") ||
              products.some(product => !product || !Array.isArray(product.variants) || !Array.isArray(product.options))) {
            throw new Error(this.errorLabel);
          }
          if (root.dataset.experience === "buy-together") {
            const looks = read("[data-pam-buy-together]");
            if (!looks || typeof looks !== "object" || Array.isArray(looks)) throw new Error(this.errorLabel);
          }
          // Fragmentos de indisponibilidade ainda podem exibir a mensagem existente, mas não ficam em cache.
          return configs.length > 0 && products.length > 0;
        } catch {
          throw new Error(this.errorLabel);
        }
      }

      async loadProduct(productUrl, view, context = {}) {
        const endpoint = this.buildEndpoint(productUrl, view);
        const key = this.buildCacheKey(endpoint, context);
        this.abortController?.abort();
        this.abortController = null;
        const now = Date.now();
        for (const [entryKey, entry] of this.cache) {
          if (entry.expiresAt <= now) this.cache.delete(entryKey);
        }
        const cached = this.cache.get(key);
        if (cached) {
          this.cache.delete(key);
          this.cache.set(key, cached); // LRU, sem prolongar a expiração.
          return cached.html;
        }
        const controller = new AbortController();
        this.abortController = controller;
        const revision = this.cacheState.revision;
        try {
          const response = await fetch(endpoint, {
            headers: { "X-Requested-With": "XMLHttpRequest" }, signal: controller.signal,
          });
          if (!response.ok) throw new Error(this.errorLabel);
          const html = await response.text();
          // Um transporte que ignore abort também não pode popular cache após troca/invalidação.
          if (controller.signal.aborted || revision !== this.cacheState.revision) {
            const error = new Error("Purchase modal: carregamento descartado");
            error.name = "AbortError";
            throw error;
          }
          const cacheable = this.validateFragment(html, endpoint);
          if (cacheable) {
            this.cache.set(key, { html, expiresAt: Date.now() + this.cacheTTL });
            while (this.cache.size > this.cacheLimit) this.cache.delete(this.cache.keys().next().value);
          }
          return html;
        } finally {
          if (this.abortController === controller) this.abortController = null;
        }
      }

    },
  );
}



if (!customElements.get("purchase-experience")) {
  customElements.define("purchase-experience", class PurchaseExperience extends HTMLElement {
      initialize() {
        if (this.initialized) return;
        this.initialized = true;
        this.destroyed = false;
        this.submissionState = "idle";
        this.lifecycleId = (this.lifecycleId || 0) + 1;
        this.abortController = new AbortController();
        this.configs = this.parseJson("[data-pam-configs]", []);
        this.products = this.parseJson("[data-pam-products]", []);
        this.looks = this.parseJson("[data-pam-buy-together]", {});
        this.productsByHandle = new Map(this.products.map((product) => [product.handle, product]));
        this.productsById = new Map(this.products.map((product) => [String(product.id), product]));
        this.experience = this.dataset.experience;
        this.body = this.querySelector("[data-pam-body]");
        this.submit = this.querySelector("[data-pam-submit]");
        this.submitLabel = this.querySelector("[data-pam-submit-label]");
        this.checkoutSubmit = this.querySelector("[data-pam-checkout]");
        this.error = this.querySelector("[data-pam-error]");
        this.priceNormalize = this.querySelector("[data-pam-price] price-normalize");
        this.discountBadge = this.querySelector("[data-pam-discount-badge]");
        this.discountValue = this.querySelector("[data-pam-discount-value]");
        this.priceContainer = this.querySelector("[data-pam-price]");
        this.galleryImage = this.querySelector(".pam-gallery__image");
        this.galleryPrevious = this.querySelector("[data-pam-gallery-previous]");
        this.galleryNext = this.querySelector("[data-pam-gallery-next]");
        this.galleryDots = this.querySelector("[data-pam-gallery-dots]");
        this.galleryStatus = this.querySelector("[data-pam-gallery-status]");
        this.galleryItems = [];
        this.galleryIndex = 0;
        this.galleryTabletQuery = window.matchMedia("(min-width: 550px) and (max-width: 1024px)");
        this.galleryTabletQuery.addEventListener("change", () => this.updateGalleryFrame(), {
          signal: this.abortController.signal,
        });
        this.steps = [];
        this.selectedTier = null;
        this.pendingFeedbackVisible = false;
        this.hasUserKitSelection = false;
        this.compositionInvalid = false;
        this.buyTogetherInvalid = false;
        this.itemPriceObservers = new Map();

        document.addEventListener(
          "click",
          (event) => {
            const coupon = event.target.closest?.(".pam-coupon-code");

            if (coupon && this.contains(coupon)) {
              this.closeItemCouponTooltips(coupon);
              return;
            }

            this.closeItemCouponTooltips();
          },
          { signal: this.abortController.signal },
        );

        const requestedId = String(this.dataset.requestedPromotionId || "");
        this.config = this.configs.find((config) => String(config.id) === requestedId) ||
          (this.experience === "buy-together" && requestedId ? null : this.configs[0]) || null;
        this.productSource =
          this.dataset.requestedProductSource ||
          this.dataset.productSource ||
          this.config?.productSources?.[0] ||
          "pdk";

        this.syncSubmitTracking();

        this.checkoutSubmit?.addEventListener("click", () => this.submitOffer({ redirectToCheckout: true }), {
          signal: this.abortController.signal,
        });
        this.submit?.addEventListener("click", () => this.submitOffer(), {
          signal: this.abortController.signal,
        });
        this.galleryPrevious?.addEventListener("click", () => this.changeGallery(-1), {
          signal: this.abortController.signal,
        });
        this.galleryNext?.addEventListener("click", () => this.changeGallery(1), {
          signal: this.abortController.signal,
        });

        this.render();
        const header = this.querySelector(".pam-product__header");
        if (header) {
          const lifecycleId = this.lifecycleId;
          const syncHeaderHeight = () => {
            if (this.destroyed || lifecycleId !== this.lifecycleId) return;
            this.style.setProperty("--pam-header-height", `${header.offsetHeight}px`);
          };
          syncHeaderHeight();
          this.headerResizeObserver = new ResizeObserver(syncHeaderHeight);
          this.headerResizeObserver.observe(header);
        }
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        // O POST mantém apenas seu snapshot/trava até responder; não é cancelado por desmontagem.
        if (this.submissionState === "awaiting-terms") this.releaseSubmission(this.operation);
        this.operation?.removeTermsListener?.();
        if (this.operation) {
          this.operation.removeTermsListener = null;
          this.operation.confirm = null;
        }
        this.operation = null;
        this.headerResizeObserver?.disconnect();
        this.singlePackPriceObserver?.disconnect();
        this.abortController?.abort();
        this.animationFrames?.forEach(id => cancelAnimationFrame(id));
        this.experienceTimers?.forEach(id => window.clearTimeout(id));
        this.animationFrames?.clear();
        this.experienceTimers?.clear();
        this.getAnimations?.({ subtree: true }).forEach(animation => animation.cancel());
        this.itemPriceObservers?.forEach(observer => observer.disconnect());
        this.itemPriceObservers?.clear();
        this.productsByHandle?.clear();
        this.productsById?.clear();
        this.steps = [];
        this.products = [];
        this.configs = [];
        this.looks = {};
        this.galleryItems = [];
        this.gallerySecondImage?.remove();
        for (const key of ["body", "submit", "submitLabel", "checkoutSubmit", "error", "priceNormalize",
          "discountBadge", "discountValue", "priceContainer", "galleryImage", "galleryPrevious", "galleryNext",
          "galleryDots", "galleryStatus", "gallerySecondImage", "galleryTabletQuery",
          "config", "selectedTier", "headerResizeObserver", "abortController",
          "discountBadgeFrame", "discountBadgeTimeout", "singlePackPriceObserver", "cacheState", "integrationEntry"]) this[key] = null;
        this.initialized = false;
      }

      requestExperienceFrame(callback) {
        if (this.destroyed) return null;
        this.animationFrames ||= new Set();
        const lifecycleId = this.lifecycleId;
        let id;
        id = requestAnimationFrame(() => {
          this.animationFrames.delete(id);
          if (!this.destroyed && lifecycleId === this.lifecycleId) callback();
        });
        this.animationFrames.add(id);
        return id;
      }

      setExperienceTimeout(callback, delay) {
        if (this.destroyed) return null;
        this.experienceTimers ||= new Set();
        const lifecycleId = this.lifecycleId;
        let id;
        id = window.setTimeout(() => {
          this.experienceTimers.delete(id);
          if (!this.destroyed && lifecycleId === this.lifecycleId) callback();
        }, delay);
        this.experienceTimers.add(id);
        return id;
      }

      parseJson(selector, fallback) {
        const element = this.querySelector(selector);
        if (!element) return fallback;

        try {
          return JSON.parse(element.textContent);
        } catch (error) {
          console.error("Purchase modal: JSON inválido", error);
          return fallback;
        }
      }

      setEyebrow(text) {
        const eyebrow = this.querySelector("[data-pam-eyebrow]");
        if (!eyebrow) return;
        eyebrow.textContent = text || "";
        eyebrow.hidden = !text;
      }

      findProduct(handle, productId = null) {
        const rawHandle = String(handle || "")
          .trim()
          .toLowerCase();
        const normalizedHandle = this.handleize(rawHandle);

        return (
          this.productsByHandle.get(rawHandle) ||
          this.productsByHandle.get(normalizedHandle) ||
          this.productsById.get(String(productId || "").split("/").pop()) ||
          null
        );
      }

      optionKind(name) {
        const normalized = this.normalizeKey(name);
        if (["cor", "color", "colour"].includes(normalized)) return "color";
        if (["tamanho", "size"].includes(normalized)) return "size";
        if (["modelo", "model"].includes(normalized)) return "model";
        return "default";
      }

      orderOptionValues(name, values) {
        if (this.optionKind(name) !== "size") return [...values];

        const order = String(this.dataset.variantOrder || "")
          .split(",")
          .map((value) => value.trim().toUpperCase())
          .filter(Boolean);

        return [...values].sort((first, second) => {
          const firstIndex = order.indexOf(String(first).trim().toUpperCase());
          const secondIndex = order.indexOf(String(second).trim().toUpperCase());
          if (firstIndex < 0 && secondIndex < 0) return 0;
          if (firstIndex < 0) return 1;
          if (secondIndex < 0) return -1;
          return firstIndex - secondIndex;
        });
      }

      normalizeKey(value) {
        return String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/\|/g, "-")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }

      handleize(value) {
        return this.normalizeKey(value);
      }

      escapeHtml(value) {
        const element = document.createElement("span");
        element.textContent = String(value || "");
        return element.innerHTML;
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message || this.dataset.requestErrorLabel;
        this.error.hidden = false;
      }

      clearError() {
        if (!this.error) return;
        this.error.textContent = "";
        this.error.hidden = true;
      }

      // selection
      selectInitialSingleProductVariant(step) {
        const variant = this.lowestEligibleVariant(step.product);

        if (!variant) return;

        step.selectedOptions = [...variant.options];
        step.variant = variant;

        step.options.querySelectorAll(".pam-option__input").forEach((input) => {
          const optionIndex = Number(input.dataset.optionIndex);
          input.checked = step.selectedOptions[optionIndex] === input.value;
        });

        step.options.querySelectorAll(".pam-option").forEach((fieldset) => {
          const optionIndex = Number(fieldset.dataset.optionIndex);
          const selectedValue = step.selectedOptions[optionIndex];

          const selectedLabel = fieldset.querySelector("[data-pam-option-selected]");

          if (selectedLabel && selectedValue) {
            selectedLabel.textContent = selectedValue;
          }
        });

        this.updateOptionAvailability(step);
        this.updateStepPrice(step);
        this.updateStepPreOrder(step);
        this.updateSingleProductQuantityRules(step);
        this.updateStepStatus(step);
        this.updateGallery(step.product, variant, true);
        this.updatePrice();
        this.updateSubmitState();
      }

      createSingleProductQuantitySelector(step) {
        const wrapper = document.createElement("div");
        wrapper.className = "pam-quantity";

        const label = document.createElement("span");
        label.className = "pam-quantity__label";
        label.textContent = "Quantidade";

        const control = document.createElement("div");
        control.className = "pam-quantity__control";

        const minus = document.createElement("button");
        minus.type = "button";
        minus.className = "pam-quantity__button";
        minus.setAttribute("aria-label", "Diminuir quantidade");
        minus.textContent = "−";

        const input = document.createElement("input");
        input.type = "number";
        input.className = "pam-quantity__input";
        input.value = String(step.quantity);
        input.inputMode = "numeric";

        const plus = document.createElement("button");
        plus.type = "button";
        plus.className = "pam-quantity__button";
        plus.setAttribute("aria-label", "Aumentar quantidade");
        plus.textContent = "+";

        const setQuantity = (nextValue) => {
          const rule = step.quantityRule || {};
          const min = Number(rule.min || 1);
          const increment = Number(rule.increment || 1);

          const numericValue = Number(nextValue);
          const baseValue = Number.isFinite(numericValue) ? numericValue : min;
          const incrementCount = Math.round((baseValue - min) / increment);
          const normalizedValue = min + Math.max(0, incrementCount) * increment;

          const configuredMax = Number(rule.max);
          const hasConfiguredMax = Number.isFinite(configuredMax) && configuredMax > 0;
          const max = hasConfiguredMax ? configuredMax : Infinity;

          const quantity = Math.min(max, Math.max(min, normalizedValue));

          step.quantity = quantity;
          input.value = String(quantity);
          minus.disabled = quantity <= min;
          plus.disabled = quantity >= max;

          this.updatePrice();
          this.updateSubmitState();
        };

        step.quantityInput = input;
        step.quantityMinus = minus;
        step.quantityPlus = plus;
        step.setQuantity = setQuantity;

        minus.addEventListener("click", () => setQuantity(step.quantity - Number(step.quantityRule?.increment || 1)), {
          signal: this.abortController.signal,
        });

        plus.addEventListener("click", () => setQuantity(step.quantity + Number(step.quantityRule?.increment || 1)), {
          signal: this.abortController.signal,
        });

        input.addEventListener("change", () => setQuantity(input.value), { signal: this.abortController.signal });

        control.append(minus, input, plus);
        wrapper.append(label, control);

        return wrapper;
      }

      updateSingleProductQuantityRules(step) {
        if (this.experience !== "single-product" || !step?.quantityInput) return;

        const rules = step.variant?.quantity_rule || {};
        const min = Math.max(1, Number(rules.min || 1));
        const increment = Math.max(1, Number(rules.increment || 1));
        const max = Number(rules.max);

        step.quantityRule = {
          min,
          increment,
          max: Number.isFinite(max) && max > 0 ? max : null,
        };

        step.quantityInput.min = String(min);
        step.quantityInput.step = String(increment);

        if (step.quantityRule.max) {
          step.quantityInput.max = String(step.quantityRule.max);
        } else {
          step.quantityInput.removeAttribute("max");
        }

        step.setQuantity?.(step.quantity);
      }

      createStep(definition, index, total, mode) {
        const { product, quantity, composition } = definition;

        const group = document.createElement("section");
        group.className = `pam-step-group pam-step-group--${mode}`;

        if (mode !== "plain") {
          const title = document.createElement("p");
          title.className = "pam-step-group__title";
          title.textContent = this.stepTitle(index, total, quantity, composition);
          group.append(title);
        }

        const element = document.createElement(mode === "plain" ? "section" : "details");

        element.className = `pam-step pam-step--${mode}`;
        element.dataset.stepIndex = String(index);
        group.append(element);

        let summary;
        let heading;
        let thumbnail = null;

        if (mode !== "plain") {
          summary = document.createElement("summary");
          summary.className = "pam-step__summary";

          thumbnail = this.createStepThumbnail(product);
          summary.append(thumbnail);

          heading = document.createElement("span");
          heading.className = "pam-step__heading";

          const isGift = this.isGiftStep(composition);

          heading.innerHTML = `
      <span class="pam-step__title">${this.escapeHtml(product.title)}</span>
      <small class="pam-step__subtitle" data-pam-step-selection>
        Selecione as opções
      </small>
    `;

          if (isGift) {
            const giftBadge = document.createElement("span");

            giftBadge.className = "pam-step__gift-badge";
            const giftQuantity = Math.max(1, Number(composition?.quantity || quantity || 1));
            giftBadge.textContent = giftQuantity > 1 ? `BRINDE · ${giftQuantity} UNIDADES` : "BRINDE";

            heading.append(giftBadge);
          }

          const price = document.createElement("div");

          price.className = "pam-step__price";
          price.dataset.pamStepPrice = "";
          price.hidden = true;

          heading.append(price);
          summary.append(heading);

          const status = document.createElement("span");

          status.className = "pam-step__status";
          status.setAttribute("aria-hidden", "true");
          status.textContent = "✓";

          summary.append(status);
          element.append(summary);
        }

        const options = document.createElement("div");

        options.className = "pam-step__options";
        element.append(options);

        const preorder = document.createElement("div");

        preorder.className = "pam-step__preorder";
        preorder.dataset.pamStepPreorder = "";
        preorder.hidden = true;

        element.append(preorder);

        /*
         * IMPORTANTE:
         * state precisa existir ANTES de createOption(),
         * preselectInitialKitColor() ou qualquer acesso a state.index.
         */
        const state = {
          group,
          element,
          summary,
          heading,
          thumbnail,
          options,
          product,
          quantity,
          composition,
          index,
          selectedOptions: Array(product.options.length).fill(null),
          variant: null,
          preorder,
          price: heading?.querySelector("[data-pam-step-price]") || null,
        };

        const isDefaultVariantProduct =
          product.options.length === 1 && product.options[0].name === "Title" && product.variants.length === 1;

        if (isDefaultVariantProduct) {
          const variant = this.firstAvailableVariant(product);

          state.variant = variant;

          if (variant) {
            state.selectedOptions = [...variant.options];
          }
        } else {
          product.options.forEach((option, optionIndex) => {
            options.append(this.createOption(state, option, optionIndex));
          });

          /*
           * Produto simples continua sendo tratado por
           * selectInitialSingleProductVariant().
           *
           * Mecânicas pré-selecionam a cor da variante elegível
           * disponível de menor preço, sem selecionar tamanho.
           */
          if (this.experience !== "single-product") {
            this.preselectInitialKitColor(state);
          }
        }

        element.addEventListener(
          "toggle",
          () => {
            if (!element.open) return;

            this.closeOtherSteps(index);

            this.updateGallery(product, this.findGalleryVariant(state), this.hasSelectedOption(state));
          },
          { signal: this.abortController.signal },
        );

        this.updateOptionAvailability(state);
        this.updateStepStatus(state);

        return state;
      }

      createOption(step, option, optionIndex) {
        const fieldset = document.createElement("fieldset");
        fieldset.className = `pam-option pam-option--${this.optionKind(option.name)}`;
        fieldset.dataset.optionIndex = String(optionIndex);

        const legend = document.createElement("legend");
        legend.className = "pam-option__name";

        legend.innerHTML = `
          <span>${this.escapeHtml(option.name)}:</span>
          <span data-pam-option-selected></span>
        `;

        fieldset.append(legend);

        const values = document.createElement("div");
        values.className = "pam-option__values";
        const orderedValues = this.orderOptionValues(option.name, option.values);

        orderedValues.forEach((value, valueIndex) => {
          const id = `PamOption-${this.dataset.productId}-${step.index}-${optionIndex}-${valueIndex}`;
          const wrapper = document.createElement("span");
          wrapper.className = "pam-option__value";
          wrapper.dataset.optionValue = value;

          const input = document.createElement("input");
          input.type = "radio";
          input.id = id;
          input.name = `pam-option-${this.dataset.productId}-${step.index}-${optionIndex}`;
          input.value = value;
          input.className = "pam-option__input";

          input.dataset.optionIndex = String(optionIndex);
          const label = document.createElement("label");
          label.htmlFor = id;
          label.className = `pam-option__label pam-option__label--${this.optionKind(option.name)}`;

          if (this.optionKind(option.name) === "color") {
            label.classList.add(`background--${this.handleize(value)}`);
            label.title = value;
            label.innerHTML = `<span class="visually-hidden">${this.escapeHtml(value)}</span>`;
          } else {
            label.textContent = value;
          }
          input.addEventListener(
            "change",
            () => {
              this.hasUserKitSelection = true;
              step.selectedOptions[optionIndex] = value;
              const optionKind = this.optionKind(option.name);

              if (optionKind === "color") {
                this.resetOptionsForColor(step, optionIndex);
              } else if (optionKind === "model") {
                this.resetSizeForModel(step);
              }
              const selectedLabel = fieldset.querySelector("[data-pam-option-selected]");

              if (selectedLabel) {
                selectedLabel.textContent = value;
              }
              this.updateOptionAvailability(step);

              step.variant = this.findVariant(step.product, step.selectedOptions);

              this.updateStepPrice(step);
              this.updateStepPreOrder(step);
              this.updateSingleProductQuantityRules(step);
              this.updateStepStatus(step);

              const galleryVariant = this.findGalleryVariant(step);

              this.updateGallery(step.product, galleryVariant, this.hasSelectedOption(step));

              this.updateStepThumbnail(step, galleryVariant);
              this.updatePrice();
              this.updateProgressiveTierPrices();
              this.updateBuyXPayYTierPrices();
              this.updateSubmitState();

              if (!this.pendingFeedbackVisible) {
                this.clearError();
              }
            },
            { signal: this.abortController.signal },
          );

          wrapper.append(input, label);

          if (
            this.optionKind(option.name) === "color" &&
            Array.isArray(step.product.new_color_values) &&
            step.product.new_color_values.includes(value)
          ) {
            const flag = document.createElement("label");
            flag.className = "pam-option__new-color";
            flag.htmlFor = id;
            flag.textContent = "new";
            wrapper.append(flag);
          }

          values.append(wrapper);
        });

        fieldset.append(values);
        return fieldset;
      }

      updateOptionAvailability(step) {
        const visibleVariants = step.product.variants.filter((variant) => !variant.hidden);

        step.options.querySelectorAll(".pam-option").forEach((fieldset) => {
          const optionIndex = Number(fieldset.dataset.optionIndex);
          const option = step.product.options[optionIndex];
          const kind = this.optionKind(option?.name);

          fieldset.querySelectorAll(".pam-option__input").forEach((input) => {
            let exists;
            let available;

            if (kind === "color") {
              // Cor nunca depende de tamanho/modelo selecionado.
              exists = visibleVariants.some((variant) => variant.options[optionIndex] === input.value);

              available = visibleVariants.some(
                (variant) => variant.available && variant.options[optionIndex] === input.value,
              );
            } else {
              exists = visibleVariants.some((variant) => {
                if (variant.options[optionIndex] !== input.value) return false;

                return step.selectedOptions.every((selected, index) => {
                  if (!selected || index === optionIndex) return true;
                  if (kind === "model" && this.optionKind(step.product.options[index]?.name) === "size") return true;

                  return variant.options[index] === selected;
                });
              });

              available = visibleVariants.some((variant) => {
                if (!variant.available || variant.options[optionIndex] !== input.value) {
                  return false;
                }

                return step.selectedOptions.every((selected, index) => {
                  if (!selected || index === optionIndex) return true;
                  if (kind === "model" && this.optionKind(step.product.options[index]?.name) === "size") return true;

                  return variant.options[index] === selected;
                });
              });
            }

            input.disabled = !available;

            input.closest(".pam-option__value")?.toggleAttribute("hidden", !exists);

            input.closest(".pam-option__value")?.classList.toggle("is-disabled", exists && !available);
          });
        });
      }

      resetSizeForModel(step) {
        if (this.lowestEligibleVariant(step.product, step.selectedOptions)) return;

        step.product.options.forEach((option, index) => {
          if (this.optionKind(option.name) === "size") {
            step.selectedOptions[index] = null;
          }
        });

        this.syncOptionInputs(step);
      }

      resetOptionsForColor(step, colorIndex) {
        const color = step.selectedOptions[colorIndex];

        if (!color) return;

        const colorVariants = step.product.variants.filter(
          (variant) => !variant.hidden && variant.available && variant.options[colorIndex] === color,
        );

        if (!colorVariants.length) return;

        const currentCombinationStillExists = colorVariants.some((variant) =>
          step.selectedOptions.every((selected, index) => {
            if (!selected || index === colorIndex) return true;

            return variant.options[index] === selected;
          }),
        );

        if (currentCombinationStillExists) {
          return;
        }

        const fallbackVariant = colorVariants[0];

        step.product.options.forEach((option, index) => {
          if (index === colorIndex) return;

          const kind = this.optionKind(option.name);

          if (!["size", "model"].includes(kind)) return;

          step.selectedOptions[index] = fallbackVariant.options[index] || null;
        });

        this.syncOptionInputs(step);
      }

      syncOptionInputs(step) {
        step.options.querySelectorAll(".pam-option").forEach((fieldset) => {
          const optionIndex = Number(fieldset.dataset.optionIndex);
          const selectedValue = step.selectedOptions[optionIndex];

          fieldset.querySelectorAll(".pam-option__input").forEach((input) => {
            input.checked = selectedValue === input.value;
          });

          const selectedLabel = fieldset.querySelector("[data-pam-option-selected]");

          if (selectedLabel) {
            selectedLabel.textContent = selectedValue || "";
          }
        });
      }

      findVariant(product, selectedOptions) {
        return PurchaseModalPricing.findVariant(product, selectedOptions);
      }

      isValidQuantity(value) {
        return (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
          Number.isSafeInteger(Number(value)) && Number(value) > 0;
      }

      findRequiredProduct(reference) {
        if (!reference) return null;
        const product = this.findProduct(reference.productHandle, reference.productId);
        // Um handle resolvido não pode substituir um ID obrigatório diferente.
        if (reference.productId && String(product?.id) !== String(reference.productId).split("/").pop()) return null;
        return product;
      }

      rejectComposition() {
        this.compositionInvalid = true;
        this.steps = [];
        this.showError(this.dataset.missingConfigLabel);
        if (this.submit) this.submit.disabled = true;
        if (this.checkoutSubmit) this.checkoutSubmit.disabled = true;
      }

      hasCompleteComposition() {
        if (this.compositionInvalid || this.buyTogetherInvalid) return false;
        const required = this.getRequiredSteps();
        return Boolean(required?.length && required.length === this.steps.length && required.every((item, index) => {
          const step = this.steps[index];
          return step.product === item.product &&
            ((!this.isCompositionLxpy() && ["progressive-discount", "buy-x-pay-y"].includes(this.experience)) || Number(step.quantity) === item.quantity);
        }));
      }

      isSelectionComplete() {
        if (!this.hasCompleteComposition()) return false;
        return this.steps.every(step => {
          const variant = step.product.variants.find(candidate => String(candidate.id) === String(step.variant?.id));
          return Boolean(variant && variant.available && !variant.hidden &&
            step.selectedOptions?.length === variant.options.length &&
            variant.options.every((value, index) => value === step.selectedOptions[index]) &&
            (this.experience !== "buy-together" || this.looks?.[String(this.config.id)]?.variant_ids?.map(String).includes(String(variant.id))));
        });
      }

      getPendingOptionFieldset(step) {
        if (!step) return null;

        const fieldsets = [...step.options.querySelectorAll(".pam-option")];

        const missing = fieldsets.find((fieldset) => {
          const optionIndex = Number(fieldset.dataset.optionIndex);
          return !step.selectedOptions[optionIndex];
        });

        if (missing) return missing;

        const invalid = fieldsets.find((fieldset) => {
          const checked = fieldset.querySelector(".pam-option__input:checked");
          return checked?.disabled;
        });

        return invalid || fieldsets[0] || null;
      }

      updatePendingHighlights() {
        this.steps.forEach((step) => {
          const incomplete = !step.variant;
          const shouldHighlightStep = this.pendingFeedbackVisible && incomplete;

          step.group?.classList.toggle("is-pending", shouldHighlightStep);
          step.element?.classList.toggle("is-pending", shouldHighlightStep);

          const missingIndexes = new Set();

          step.product.options.forEach((option, index) => {
            if (!step.selectedOptions[index]) {
              missingIndexes.add(index);
            }
          });

          if (incomplete && missingIndexes.size === 0) {
            step.options.querySelectorAll(".pam-option").forEach((fieldset) => {
              const checked = fieldset.querySelector(".pam-option__input:checked");

              if (checked?.disabled) {
                missingIndexes.add(Number(fieldset.dataset.optionIndex));
              }
            });
          }

          step.options.querySelectorAll(".pam-option").forEach((fieldset) => {
            const optionIndex = Number(fieldset.dataset.optionIndex);
            const pending = this.pendingFeedbackVisible && incomplete && missingIndexes.has(optionIndex);

            fieldset.classList.toggle("is-pending", pending);

            if (pending) {
              fieldset.setAttribute("aria-invalid", "true");
            } else {
              fieldset.removeAttribute("aria-invalid");
            }
          });
        });
      }

      focusFirstPendingSelection() {
        const step = this.steps.find((candidate) => !candidate.variant);

        if (!step) return;

        if (step.element.tagName === "DETAILS") {
          this.openStep(step.index);
        }

        const fieldset = this.getPendingOptionFieldset(step);

        this.requestExperienceFrame(() => {
          const target = step.group;

          const focusTarget =
            [...(fieldset?.querySelectorAll(".pam-option__input") || [])].find(
              (input) => !input.disabled && !input.closest(".pam-option__value")?.hasAttribute("hidden"),
            ) ||
            step.summary ||
            step.element;

          target?.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });

          focusTarget?.focus?.({
            preventScroll: true,
          });
        });
      }

      handleIncompleteSelection() {
        this.pendingFeedbackVisible = true;

        // this.showError(
        //   this.dataset.incompleteLabel || "Ainda existem opções pendentes. Escolha todas as opções para continuar.",
        // );

        this.updatePendingHighlights();
        this.focusFirstPendingSelection();
      }

      updateStepStatus(step) {
        const complete = Boolean(step.variant);

        step.element.classList.toggle("is-complete", complete);
        step.group?.classList.toggle("is-complete", complete);

        const selection = step.element.querySelector("[data-pam-step-selection]");
        if (selection) {
          selection.textContent = complete ? step.variant.options.join(" · ") : "Selecione as opções";
        }

        const status = step.summary?.querySelector(".pam-step__status");
        if (status) {
          status.classList.toggle("is-complete", complete);
          status.setAttribute("data-state", complete ? "complete" : "pending");
        }
      }

      openStep(index) {
        const step = this.steps[index];
        if (!step) return;
        if (step.element.tagName === "DETAILS") step.element.open = true;
        this.updateGallery(step.product, this.findGalleryVariant(step), this.hasSelectedOption(step));
      }

      closeOtherSteps(activeIndex) {
        this.steps.forEach((step, index) => {
          if (index !== activeIndex && step.element.tagName === "DETAILS") step.element.open = false;
        });
      }

      updateSubmitState() {
        if (!this.submit || this.destroyed) return;

        const complete = this.isSelectionComplete();
        const locked = Boolean(this.submissionState && this.submissionState !== "idle");
        // Classes CSS são apenas apresentação, nunca a trava de envio.
        this.submit.disabled = locked || Boolean(this.compositionInvalid || this.buyTogetherInvalid);

        this.submit.dataset.pamIncomplete = String(!complete);

        if (this.checkoutSubmit) {
          this.checkoutSubmit.disabled = this.submit.disabled;
        }

        if (complete && this.pendingFeedbackVisible) {
          this.pendingFeedbackVisible = false;
          this.clearError();
        }

        this.updatePendingHighlights();
        this.updateSubmitLabel();
      }

      lowestEligibleVariant(product, selectedOptions = []) {
        return PurchaseModalPricing.lowestEligibleVariant(product, selectedOptions);
      }

      firstAvailableVariant(product) {
        return PurchaseModalPricing.firstAvailableVariant(product);
      }

      preselectInitialKitColor(step) {
        if (!step?.product) return;

        const colorIndex = step.product.options.findIndex((option) => this.optionKind(option.name) === "color");

        if (colorIndex < 0 || step.selectedOptions[colorIndex]) return;

        const cheapestVariant = this.lowestEligibleVariant(step.product);
        const firstVisibleAvailableColor = cheapestVariant?.options[colorIndex];

        if (!firstVisibleAvailableColor) return;

        step.selectedOptions[colorIndex] = firstVisibleAvailableColor;

        step.options.querySelectorAll(".pam-option__input").forEach((input) => {
          if (Number(input.dataset.optionIndex) !== colorIndex) return;

          input.checked = input.value === firstVisibleAvailableColor;
        });

        const colorFieldset = step.options.querySelector(`.pam-option[data-option-index="${colorIndex}"]`);

        const selectedLabel = colorFieldset?.querySelector("[data-pam-option-selected]");

        if (selectedLabel) {
          selectedLabel.textContent = firstVisibleAvailableColor;
        }

        this.updateOptionAvailability(step);

        // Só existe variante selecionada quando TODAS as opções estão completas.
        step.variant = this.findVariant(step.product, step.selectedOptions);

        const galleryVariant = this.findGalleryVariant(step);

        this.updateStepThumbnail(step, galleryVariant);
        this.updateStepPrice(step);
        this.updateStepPreOrder(step);
        this.updateStepStatus(step);
      }

      // mechanics
      render() {
        if (!this.body) return;
        this.body.replaceChildren();
        if (!this.config) return this.rejectComposition();

        if (this.experience === "buy-together" && !this.prepareBuyTogether()) {
          this.steps = [];
          this.buyTogetherInvalid = true;
          this.showError("Este look não está disponível com as variantes selecionadas. Escolha outro look.");
          if (this.submit) this.submit.disabled = true;
          if (this.checkoutSubmit) this.checkoutSubmit.disabled = true;
          return;
        }

        if (!this.getRequiredSteps()) return this.rejectComposition();

        switch (this.experience) {
          case "single-product":
            this.renderSingleProduct();
            break;
          case "mono-product-kit":
            this.renderMonoProductKit();
            break;
          case "multi-product-kit":
          case "buy-and-save":
          case "buy-together":
            this.renderComposition();
            break;
          case "progressive-discount":
            this.renderProgressive();
            break;
          case "buy-x-pay-y":
            if (this.isCompositionLxpy()) this.renderComposition();
            else this.renderBuyXPayY();
            break;
          default:
            this.showError(this.dataset.missingConfigLabel);
        }

        this.updateSubmitState();
        this.updatePrice();
      }

      renderSingleProduct() {
        const product = this.products[0];

        if (!product) {
          this.showError(this.dataset.missingConfigLabel);
          return;
        }

        const step = this.createStep(
          {
            product,
            quantity: 1,
            composition: null,
          },
          0,
          1,
          "plain",
        );

        this.steps = [step];
        this.body.append(step.group);

        this.selectInitialSingleProductVariant(step);

        const hasPacks = this.mountSingleProductPacks();

        if (!hasPacks) {
          this.body.append(this.createSingleProductQuantitySelector(step));
        }

        this.setEyebrow("");
      }

      mountSingleProductPacks() {
        const template = this.querySelector("[data-pam-single-packs-template]");

        if (!template?.content?.firstElementChild || !this.body) {
          return false;
        }

        this.body.append(template.content.cloneNode(true));
        this.singlePackPriceObserver?.disconnect();
        this.singlePackPriceObserver = new MutationObserver(() => this.updateSingleProductPackPrices());
        const prices = this.querySelectorAll("[data-pam-single-pack] price-normalize");
        prices.forEach(price => this.singlePackPriceObserver.observe(price, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ["data-type", "data-price-object-discount-value", "data-pristine-price", "data-compare-at-price",
            "data-kit-discount", "data-pix-discount-value", "data-final-price-applied"],
        }));
        this.updateSingleProductPackPrices();
        return true;
      }

      updateSingleProductPackPrices() {
        if (this.destroyed || this.experience !== "single-product") return;
        this.querySelectorAll("[data-pam-single-pack]").forEach(pack => {
          const price = pack.querySelector("price-normalize");
          const badge = pack.querySelector("[data-pam-single-pack-discount]");
          if (!price || !badge) return;
          const values = PurchaseModalPriceAdapter.singlePackValues(price);
          const saving = Boolean(values && values.referenceValue > values.finalValue);
          pack.classList.toggle("has-savings", saving);
          badge.hidden = !values || values.discount <= 0;
          badge.textContent = badge.hidden ? "" : `${values.discount}% OFF`;
          // OnlyPrice omits markdown when reference equals pristine price, even with an offer.
          // Keep the supplied catalog reference without changing shared price rendering.
          const container = price.querySelector(".price-container");
          if (saving && container) {
            let markdown = container.querySelector(".markdown-price");
            if (!markdown) {
              markdown = document.createElement("span");
              markdown.className = "markdown-price";
              const from = container.querySelector(".price-varies");
              container.insertBefore(markdown, from ? from.nextSibling : container.firstChild);
            }
            const reference = this.formatMoney(values.referenceValue);
            if (markdown.textContent !== reference) markdown.textContent = reference;
          }
        });
      }

      renderMonoProductKit() {
        const steps = this.getRequiredSteps();
        if (!steps) return this.rejectComposition();

        this.steps = steps.map((step, index) => this.createStep(step, index, steps.length, "accordion"));
        this.steps.forEach((step) => this.body.append(step.group));
        // this.openStep(0);
        this.setEyebrow(`${steps.length} ${steps.length === 1 ? "escolha" : "escolhas"} para completar o kit`);
        this.syncInitialKitGallery();
      }

      renderComposition() {
        const definitions = this.getRequiredSteps();
        if (!definitions) return this.rejectComposition();
        this.steps = definitions.map((step, index) =>
          this.createStep(step, index, definitions.length, "card"));

        this.steps.forEach((step) => this.body.append(step.group));

        const look = this.looks?.[String(this.config.id)];
        if (this.experience === "buy-together" && look) {
          const title = this.querySelector(".pam-product__title");
          if (look.title && title) title.textContent = look.title;
          this.setEyebrow(look.subtitle || "Complete o look");
          this.syncSubmitTracking();
        } else if (this.experience === "buy-and-save" || this.isCompositionLxpy()) {
          this.setEyebrow("Escolha os itens e receba a vantagem");
        } else {
          this.setEyebrow("Escolha as opções de cada item do conjunto");
        }
        this.syncInitialKitGallery();
      }

      renderProgressive() {
        const product = this.config.productHandle || this.config.productId
          ? this.findRequiredProduct(this.config)
          : this.products[0];

        if (!product) {
          this.showError(this.dataset.missingConfigLabel);
          return;
        }

        const step = this.createStep(
          {
            product,
            quantity: 1,
            composition: null,
          },
          0,
          1,
          "plain",
        );

        this.steps = [step];

        // Options primeiro, como no Leve-Pague.
        this.body.append(step.group);
        this.body.append(this.createTierSelector(this.config.rules || [], "progressive"));

        this.setEyebrow("");
        this.updateProgressiveTierPrices();
        this.syncInitialKitGallery();
      }

      renderBuyXPayY() {
        const configs = this.configs.length ? this.configs : [this.config];
        const firstConfig = configs[0];
        const firstComposition = firstConfig.productComposition?.[0];

        const product = this.findRequiredProduct(firstComposition || firstConfig);

        if (!product) {
          this.showError(this.dataset.missingConfigLabel);
          return;
        }

        const step = this.createStep(
          {
            product,
            quantity: 1,
            composition: firstComposition || null,
          },
          0,
          1,
          "plain",
        );

        this.steps = [step];

        // Options primeiro.
        this.body.append(step.group);

        // Quantidade sempre depois dos options.
        this.body.append(this.createBuyXPayYQuantitySelector(configs));

        this.updateSubmitLabel();
        this.updateBuyXPayYTierPrices();
        this.syncInitialKitGallery();
      }

      getProgressiveTierPrice(rule) {
        const product = this.steps[0]?.product;
        const variant = this.steps[0]?.variant || this.firstAvailableVariant(product);

        if (!variant) return null;

        const quantity = Math.max(1, Number(rule.quantityRequiredForDiscount || 1));
        const discount = Math.max(0, Number(rule.discountPercentage || 0));
        const baseTotal = Number(variant.price || 0) * quantity;
        const total = Math.round(baseTotal * (1 - discount / 100));

        return {
          quantity,
          total,
        };
      }

      updateProgressiveTierPrices() {
        if (this.experience !== "progressive-discount") return;

        this.querySelectorAll("[data-pam-progressive-tier]").forEach((label) => {
          const index = Number(label.dataset.pamProgressiveTier);
          const rule = this.config.rules?.[index];
          const totalElement = label.querySelector("[data-pam-progressive-tier-price]");

          if (!rule || !totalElement) return;

          const tier = this.getProgressiveTierPrice(rule);

          totalElement.textContent = tier ? `${this.formatMoney(tier.total, true)}` : "";
        });
      }

      createBuyXPayYQuantitySelector(configs) {
        const wrapper = document.createElement("fieldset");
        wrapper.className = "pam-tiers pam-tiers--buy-x-pay-y";

        const legend = document.createElement("legend");
        legend.className = "pam-tiers__title";
        legend.textContent = "Quantidade";
        wrapper.append(legend);

        const list = document.createElement("div");
        list.className = "pam-tiers__list";

        const entries = [
          ...(this.shouldShowBuyXPayYSingleUnit()
            ? [
                {
                  type: "unit",
                  id: "unit",
                  label: "1 unidade",
                  quantity: 1,
                },
              ]
            : []),

          ...configs.map((config) => ({
            type: "promotion",
            id: String(config.id),
            config,
            rule: config.rules?.[0] || {},
            label: config.rules?.[0]?.name || "Oferta especial",
          })),
        ];

        const promotionIndexes = entries
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => item.type === "promotion");

        const bestPromotionIndex = promotionIndexes.reduce((bestIndex, current) => {
          if (bestIndex === -1) {
            return current.index;
          }

          const bestConfig = entries[bestIndex]?.config;

          const bestDiscount = this.getBuyXPayYEffectiveDiscount(bestConfig);

          const currentDiscount = this.getBuyXPayYEffectiveDiscount(current.item.config);

          return currentDiscount > bestDiscount ? current.index : bestIndex;
        }, -1);

        entries.forEach((entry, index) => {
          const id = `PamBuyXPayY-${this.dataset.productId}-${entry.id}`;

          const input = document.createElement("input");
          input.type = "radio";
          input.id = id;
          input.name = `pam-buy-x-pay-y-${this.dataset.productId}`;
          input.className = "pam-tiers__input";

          // Seleciona por padrão a mecânica promocional
          // com o maior desconto efetivo.
          input.checked = index === bestPromotionIndex;

          const label = document.createElement("label");
          label.className = "pam-tiers__label";
          label.htmlFor = id;
          label.dataset.pamBuyXPayYTier = entry.id;

          label.innerHTML = `
            <span class="pam-tiers__quantity-value">
              ${this.escapeHtml(entry.label)}
            </span>

            <span class="pam-tiers__price-group">
              <span
                class="pam-tiers__discount-price"
                data-pam-buy-x-pay-y-discount
                hidden
              ></span>

              <span
                class="pam-tiers__unit-price"
                data-pam-tier-unit-price
              ></span>
            </span>
          `;

          input.addEventListener(
            "change",
            () => {
              if (entry.type === "unit") {
                this.selectedTier = {
                  type: "unit",
                  quantity: 1,
                  config: null,
                  rule: null,
                };
              } else {
                this.selectedTier = {
                  type: "promotion",
                  config: entry.config,
                  rule: entry.rule,
                };

                this.config = entry.config;
                this.syncSubmitTracking();
                this.productSource = this.resolveProductSource(entry.config);
              }

              this.updatePrice();
              this.updateBuyXPayYTierPrices();
              this.updateSubmitState();
              this.updateSubmitLabel();
            },
            { signal: this.abortController.signal },
          );

          list.append(input, label);

          if (index === bestPromotionIndex) {
            this.selectedTier = {
              type: "promotion",
              config: entry.config,
              rule: entry.rule,
            };

            // Importante quando existem várias configs:
            // deixa this.config coerente com o tier inicialmente selecionado.
            this.config = entry.config;
            this.productSource = this.resolveProductSource(entry.config);
          }
        });

        wrapper.append(list);

        return wrapper;
      }

      getSelectedBuyXPayYQuantity() {
        if (this.selectedTier?.type === "unit") {
          return 1;
        }

        return this.getBuyXPayYQuantity(this.selectedTier?.config || this.config);
      }

      getBuyXPayYTierPrice(tier) {
        return PurchaseModalPricing.getBuyXPayYTierPrice(this.getPricingState(), tier);
      }

      updateBuyXPayYTierPrices() {
        if (this.experience !== "buy-x-pay-y") return;

        this.querySelectorAll("[data-pam-buy-x-pay-y-tier]").forEach((label) => {
          const tierId = String(label.dataset.pamBuyXPayYTier || "");

          const unitPriceElement = label.querySelector("[data-pam-tier-unit-price]");

          const discountElement = label.querySelector("[data-pam-buy-x-pay-y-discount]");

          if (!unitPriceElement) return;

          const tier =
            tierId === "unit"
              ? {
                  type: "unit",
                }
              : {
                  type: "promotion",
                  config: this.configs.find((config) => String(config.id) === tierId),
                };

          if (tier.type === "promotion") {
            tier.rule = tier.config?.rules?.[0] || {};
          }

          const price = this.getBuyXPayYTierPrice(tier);

          if (!price) {
            unitPriceElement.textContent = "";

            if (discountElement) {
              discountElement.textContent = "";
              discountElement.hidden = true;
            }

            return;
          }

          unitPriceElement.innerHTML = `${this.formatMoney(price.unitPrice, true)} <small>/un.</small>`;

          if (!discountElement) return;

          const discount = this.getBuyXPayYDisplayedDiscount(price);

          if (discount > 0) {
            discountElement.textContent = `${discount}% OFF`;
            discountElement.hidden = false;
          } else {
            discountElement.textContent = "";
            discountElement.hidden = true;
          }
        });
      }

      createTierSelector(entries, type) {
        const wrapper = document.createElement("fieldset");
        wrapper.className = "pam-tiers";
        if (type === "progressive") {
          wrapper.classList.add("pam-tiers--progressive");
        }

        const legend = document.createElement("legend");
        legend.className = "pam-tiers__title";
        legend.textContent = "Quantidade";
        wrapper.append(legend);

        const list = document.createElement("div");
        list.className = "pam-tiers__list";

        const defaultIndex =
          type === "progressive"
            ? entries.reduce((bestIndex, entry, index) => {
                const bestDiscount = Number(entries[bestIndex]?.discountPercentage || 0);

                const currentDiscount = Number(entry?.discountPercentage || 0);

                return currentDiscount > bestDiscount ? index : bestIndex;
              }, 0)
            : 0;

        entries.forEach((entry, index) => {
          const config = type === "progressive" ? this.config : entry;
          const rule = type === "progressive" ? entry : entry.rules?.[0];

          if (!rule) return;

          const id = `PamTier-${this.dataset.productId}-${type}-${index}`;
          const input = document.createElement("input");

          input.type = "radio";
          input.name = `pam-tier-${this.dataset.productId}-${type}`;
          input.id = id;
          input.className = "pam-tiers__input";
          input.checked = index === defaultIndex;

          const label = document.createElement("label");
          label.className = "pam-tiers__label";
          label.htmlFor = id;

          const quantity = Number(rule.quantityRequiredForDiscount || 1);
          const quantityLabel = `${quantity} ${quantity === 1 ? "unidade" : "unidades"}`;

          if (type === "progressive") {
            label.dataset.pamProgressiveTier = String(index);

            label.innerHTML = `
              <span class="pam-tiers__quantity-value">
                ${quantityLabel}
              </span>
              <span class="pam-tiers__discount-price">
                ${Number(rule.discountPercentage || 0)}% OFF
              </span>
              <span
                class="pam-tiers__total-price"
                data-pam-progressive-tier-price
              ></span>
            `;
          } else {
            label.dataset.pamTierId = String(config.id);
            label.innerHTML = `
              <span class="pam-tiers__quantity-value"><strong>${this.escapeHtml(rule.name || quantityLabel)}</strong></span>
              <strong class="pam-tiers__unit-price" data-pam-tier-unit-price hidden></strong>
            `;
          }

          input.addEventListener(
            "change",
            () => {
              this.selectedTier = { config, rule };
              this.config = config;
              this.syncSubmitTracking();
              this.productSource = this.resolveProductSource(config);

              this.updatePrice();
              this.updateProgressiveTierPrices();
              this.updateBuyXPayYTierPrices();
              this.updateSubmitState();
              this.updateSubmitLabel();
            },
            { signal: this.abortController.signal },
          );

          list.append(input, label);

          if (index === defaultIndex) {
            this.selectedTier = { config, rule };
          }
        });
        const tierCount = list.querySelectorAll(".pam-tiers__input").length;

        wrapper.classList.toggle("pam-tiers--single", tierCount === 1);

        wrapper.append(list);
        if (type === "progressive") {
          this.updateProgressiveTierPrices();
        }
        return wrapper;
      }

      prepareBuyTogether() {
        const look = this.looks?.[String(this.config?.id)];
        const composition = this.config?.productComposition;
        if (!look || !Array.isArray(look.variant_ids) || !look.variant_ids.length ||
            !Array.isArray(composition) || !composition.length) return false;

        const allowed = new Set(look.variant_ids.map(String));
        const products = new Map(this.products.map((product) => [String(product.id), product]));
        (look.products || []).forEach((product) => products.set(String(product.id), product));
        this.products = [...products.values()].map((product) => ({
          ...product,
          variants: product.variants.map((variant) => ({
            ...variant,
            hidden: variant.hidden || !allowed.has(String(variant.id)),
          })),
          media: (product.media || []).filter((media) => !media.variant_ids?.length ||
            media.variant_ids.some((id) => allowed.has(String(id)))),
        }));
        this.productsByHandle = new Map(this.products.map((product) => [product.handle, product]));
        this.productsById = new Map(this.products.map((product) => [String(product.id), product]));

        return composition.every((item) => {
          if (!item) return false;
          const product = this.findProduct(item.productHandle, item.productId);
          return product?.variants.some((variant) => !variant.hidden && variant.available);
        });
      }

      isCompositionLxpy() {
        const config = this.selectedTier?.config || this.config;
        const composition = config?.productComposition;
        const productId = String(this.dataset.productId);
        return this.experience === "buy-x-pay-y" &&
          String(config?.productPDKId).split("/").pop() === productId &&
          Array.isArray(composition) && composition.length > 0 &&
          composition.every(item => item && this.isValidQuantity(item.quantity) &&
            String(item.productId).split("/").pop() === productId) &&
          composition.reduce((sum, item) => sum + Number(item.quantity), 0) > 1;
      }

      getRequiredSteps() {
        const config = this.selectedTier?.config || this.config;
        if (!config) return null;
        const composition = config.productComposition;
        if (composition != null && !Array.isArray(composition)) return null;
        const rule = this.selectedTier?.rule || config.rules?.[0] || {};
        const make = (product, quantity, item = null) => product && this.isValidQuantity(quantity)
          ? { product, quantity: Number(quantity), composition: item } : null;

        if (this.experience === "single-product") {
          const step = make(this.products[0], this.steps[0]?.quantity ?? 1);
          return step ? [step] : null;
        }
        if (this.experience === "progressive-discount") {
          const product = config.productHandle || config.productId ? this.findRequiredProduct(config) : this.products[0];
          const step = make(product, rule.quantityRequiredForDiscount);
          return step ? [step] : null;
        }
        if (this.isCompositionLxpy()) {
          const definitions = composition.map(item => make(this.findRequiredProduct(item), item.quantity, item));
          if (definitions.some(step => !step)) return null;
          return definitions.flatMap(step => Array.from({ length: step.quantity }, () => ({ ...step, quantity: 1 })));
        }
        if (this.experience === "buy-x-pay-y") {
          const product = this.findRequiredProduct(composition?.[0] || config);
          // Esta experiência repete uma variante; não pode representar produtos distintos.
          if (composition?.some(item => !item || !this.isValidQuantity(item.quantity) ||
              this.findRequiredProduct(item) !== product)) return null;
          const quantity = this.selectedTier?.type === "unit" ? 1 : composition?.length
            ? composition.reduce((sum, item) => sum + Number(item.quantity), 0) : rule.quantityRequiredForDiscount;
          const step = make(product, quantity, composition?.[0]);
          return step ? [step] : null;
        }
        if (composition?.length) {
          const entries = [...composition];
          if (this.experience === "buy-together") {
            const currentId = String(this.dataset.productId);
            entries.sort((a, b) => Number(String(b?.productId).split("/").pop() === currentId) -
              Number(String(a?.productId).split("/").pop() === currentId));
          }
          const definitions = entries.map(item => item && make(this.findRequiredProduct(item), item.quantity, item));
          if (definitions.some(step => !step)) return null;
          return this.experience === "mono-product-kit"
            ? definitions.flatMap(step => Array.from({ length: step.quantity }, () => ({ ...step, quantity: 1 })))
            : definitions;
        }
        if (this.experience !== "mono-product-kit") return null;
        const product = this.findRequiredProduct(config);
        const distribution = rule.productDistribution;
        if (distribution != null && !Array.isArray(distribution)) return null;
        if (distribution?.length) {
          const steps = distribution.map(quantity => make(product, quantity));
          return steps.every(Boolean) ? steps : null;
        }
        if (!product || !this.isValidQuantity(rule.quantityRequiredForDiscount)) return null;
        return Array.from({ length: Number(rule.quantityRequiredForDiscount) }, () => make(product, 1));
      }

      isGiftStep(composition) {
        return (this.experience === "buy-and-save" && Number(composition?.discountPercentageWeight || 0) >= 100) ||
          (this.isCompositionLxpy() && this.getStepDiscount({ composition }) >= 100);
      }

      stepTitle(index, total, quantity, composition = null) {
        if (this.isGiftStep(composition)) {
          return "🎁 Escolha seu brinde:";
        }

        if (quantity > 1) {
          return `Escolha ${quantity} itens:`;
        }

        if (total === 1) {
          return "Escolha o item:";
        }

        const ordinals = ["primeiro", "segundo", "terceiro", "quarto", "quinto"];

        const ordinal = ordinals[index] || `${index + 1}º`;

        return `Escolha o ${ordinal} item:`;
      }

      shouldShowBuyXPayYSingleUnit() {
        const templateSuffix = String(this.dataset.templateSuffix || "")
          .trim()
          .toLowerCase();

        return !templateSuffix || templateSuffix !== "new-buy-x-pay-y";
      }

      // gallery
      hasSelectedOption(step) {
        return step.selectedOptions.some(Boolean);
      }

      findGalleryVariant(step) {
        const matchingVariant = step.product.variants.find((variant) => {
          if (variant.hidden || !variant.available) return false;

          return step.selectedOptions.every((selectedValue, index) => {
            return !selectedValue || variant.options[index] === selectedValue;
          });
        });

        return matchingVariant || this.firstAvailableVariant(step.product);
      }

      getVisibleMedia(product) {
        const visibleVariantIds = new Set(
          product.variants
            .filter((variant) => !variant.hidden && variant.available)
            .map((variant) => String(variant.id)),
        );

        return (product.media || []).filter((media) => {
          const mediaVariantIds = (media.variant_ids || []).map(String);

          return !mediaVariantIds.length || mediaVariantIds.some((id) => visibleVariantIds.has(id));
        });
      }

      getDefaultMedia(product) {
        const variant = this.firstAvailableVariant(product);

        if (variant?.featured_image?.url) {
          return variant.featured_image;
        }

        const visibleMedia = this.getVisibleMedia(product);

        return (
          visibleMedia.find((media) => (media.variant_ids || []).map(String).includes(String(variant?.id))) ||
          visibleMedia[0] ||
          null
        );
      }

      updateGallery(product, variant, hasSelection = false) {
        if (!this.galleryImage || !product) return;

        if (!hasSelection || !variant) {
          const image = this.getDefaultMedia(product);

          if (image?.url) {
            this.galleryImage.src = image.url;
            this.galleryImage.srcset = "";
            this.galleryImage.alt = image.alt || product.title;
          }

          this.galleryItems = [];
          this.galleryIndex = 0;
          this.galleryDots?.replaceChildren();
          this.syncTabletGallery();

          if (this.galleryPrevious) this.galleryPrevious.hidden = true;
          if (this.galleryNext) this.galleryNext.hidden = true;
          if (this.galleryDots) this.galleryDots.hidden = true;
          if (this.galleryStatus) this.galleryStatus.textContent = "";

          return;
        }

        const visibleMedia = this.getVisibleMedia(product);
        const colorIndex = product.options.findIndex((option) => this.optionKind(option.name) === "color");
        const color = colorIndex >= 0 ? variant.options?.[colorIndex] : null;

        const colorMedia = color ? visibleMedia.filter((media) => this.mediaMatchesColor(media, color)) : [];

        const variantMedia = visibleMedia.filter((media) =>
          (media.variant_ids || []).map(String).includes(String(variant.id)),
        );

        let items = colorMedia.length ? colorMedia : variantMedia;

        if (!items.length && variant.featured_image?.url) {
          items = [variant.featured_image];
        }

        this.galleryItems = items.filter(
          (media, index, collection) =>
            media?.url &&
            collection.findIndex((item) => String(item.id) === String(media.id) || item.url === media.url) === index,
        );

        this.galleryIndex = 0;
        this.renderGalleryDots();
        this.updateGalleryFrame();
      }

      changeGallery(direction) {
        if (this.galleryItems.length < 2) return;
        this.galleryIndex = (this.galleryIndex + direction + this.galleryItems.length) % this.galleryItems.length;
        this.updateGalleryFrame();
      }

      renderGalleryDots() {
        if (!this.galleryDots) return;
        this.galleryDots.replaceChildren();

        this.galleryItems.forEach((media, index) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = "pam-gallery__dot";
          dot.setAttribute("aria-label", `Mostrar imagem ${index + 1}`);
          dot.addEventListener(
            "click",
            () => {
              this.galleryIndex = index;
              this.updateGalleryFrame();
            },
            { signal: this.abortController.signal },
          );
          this.galleryDots.append(dot);
        });
      }

      syncTabletGallery() {
        const media = this.galleryItems[(this.galleryIndex + 1) % this.galleryItems.length];
        if (!this.galleryTabletQuery?.matches || this.galleryItems.length < 2 || !media?.url || !this.galleryImage) {
          this.gallerySecondImage?.remove();
          this.gallerySecondImage = null;
          return;
        }

        if (!this.gallerySecondImage) {
          this.gallerySecondImage = document.createElement("img");
          this.gallerySecondImage.className = "pam-gallery__image pam-gallery__image--secondary";
          this.gallerySecondImage.decoding = "async";
          this.galleryImage.after(this.gallerySecondImage);
        }

        this.gallerySecondImage.src = media.url;
        this.gallerySecondImage.alt = media.alt || "";
      }

      updateGalleryFrame() {
        this.syncTabletGallery();
        const media = this.galleryItems[this.galleryIndex];
        if (!media || !this.galleryImage) return;

        this.galleryImage.src = media.url;
        this.galleryImage.srcset = "";
        this.galleryImage.alt = media.alt || "";

        const hasMultiple = this.galleryItems.length > 1;
        if (this.galleryPrevious) this.galleryPrevious.hidden = !hasMultiple;
        if (this.galleryNext) this.galleryNext.hidden = !hasMultiple;
        if (this.galleryDots) this.galleryDots.hidden = !hasMultiple;

        this.galleryDots?.querySelectorAll(".pam-gallery__dot").forEach((dot, index) => {
          const active = index === this.galleryIndex;
          dot.classList.toggle("is-active", active);
          dot.toggleAttribute("aria-current", active);
        });

        if (this.galleryStatus) {
          this.galleryStatus.textContent = this.gallerySecondImage
            ? `Imagens ${this.galleryIndex + 1} e ${(this.galleryIndex + 1) % this.galleryItems.length + 1} de ${this.galleryItems.length}`
            : `Imagem ${this.galleryIndex + 1} de ${this.galleryItems.length}`;
        }
      }

      syncInitialKitGallery() {
        const step = this.steps[0];

        if (!step || !this.hasSelectedOption(step)) return;

        const variant = this.findGalleryVariant(step);

        this.updateGallery(step.product, variant, true);
        this.updateStepThumbnail(step, variant);
      }

      mediaMatchesColor(media, color) {
        const mediaKey = this.normalizeKey(media.alt);
        const colorKey = this.normalizeKey(color);
        if (!mediaKey || !colorKey) return false;
        return (
          mediaKey === colorKey ||
          mediaKey.startsWith(`${colorKey}-`) ||
          mediaKey.endsWith(`-${colorKey}`) ||
          mediaKey.includes(`-${colorKey}-`)
        );
      }

      createStepThumbnail(product) {
        const wrapper = document.createElement("span");
        wrapper.className = "pam-step__media";

        const imageSource = this.getDefaultMedia(product);

        if (imageSource?.url) {
          const image = document.createElement("img");
          image.src = imageSource.url;
          image.alt = imageSource.alt || product.title;
          image.loading = "lazy";
          wrapper.append(image);
        }

        return wrapper;
      }

      updateStepThumbnail(step, variant) {
        const image = step.thumbnail?.querySelector("img");

        if (!image) return;

        const visibleMedia = this.getVisibleMedia(step.product);
        const colorIndex = step.product.options.findIndex((option) => this.optionKind(option.name) === "color");

        const color = colorIndex >= 0 ? variant?.options?.[colorIndex] : null;

        const colorMedia = color ? visibleMedia.filter((media) => this.mediaMatchesColor(media, color)) : [];

        const variantMedia = variant
          ? visibleMedia.filter((media) => (media.variant_ids || []).map(String).includes(String(variant.id)))
          : [];

        const media = variant?.featured_image?.url
          ? variant.featured_image
          : colorMedia[0] || variantMedia[0] || this.getDefaultMedia(step.product);

        if (!media?.url) return;

        image.src = media.url;
        image.srcset = "";
        image.alt = media.alt || step.product.title;
      }

      // pricing
      scheduleDiscountBadgeUpdate() {
        cancelAnimationFrame(this.discountBadgeFrame);
        window.clearTimeout(this.discountBadgeTimeout);
        this.animationFrames?.delete(this.discountBadgeFrame);
        this.experienceTimers?.delete(this.discountBadgeTimeout);

        this.discountBadgeFrame = this.requestExperienceFrame(() => {
          this.discountBadgeTimeout = this.setExperienceTimeout(() => {
            this.updateDiscountBadgeFromPrice();
          }, 80);
        });
      }

      updateDiscountBadgeFromPrice() {
        if (!this.priceContainer || !this.discountBadge || !this.discountValue) return;

        const hasMarkdown = this.priceContainer.querySelector(".markdown-price");
        const presentation = this.priceContainer.querySelector(".coupon-price") ? 'coupon'
          : this.priceContainer.querySelector(".pix-price > .init-price") ? 'pix'
          : this.priceContainer.querySelector(".original-price") ? 'original' : null;
        const values = presentation && PurchaseModalPriceAdapter.badgeValues(this.priceNormalize, presentation);
        if (!hasMarkdown || !values || !Number.isFinite(values.referenceValue) ||
            !Number.isFinite(values.finalValue) || values.referenceValue <= values.finalValue) {
          this.discountBadge.hidden = true;
          return;
        }
        const discount = PurchaseModalPricing.displayedDiscount(values.referenceValue, values.finalValue);
        this.discountValue.textContent = String(discount);
        this.discountBadge.hidden = discount <= 0;
      }

      updateStepPrice(step) {
        if (this.isGiftStep(step.composition)) {
          step.price?.replaceChildren();
          if (step.price) step.price.hidden = true;
          return;
        }

        if (!step.variant || !step.price) {
          if (step.price) step.price.hidden = true;
          return;
        }

        const template = this.querySelector(
          `[data-pam-item-price-template][data-product-handle="${step.product.handle}"]`,
        );

        if (!template) return;

        if (!step.price.querySelector("price-normalize")) {
          step.price.append(template.content.cloneNode(true));
        }

        const priceNormalize = step.price.querySelector("price-normalize");
        const quantity = Number(step.quantity || 1);
        const discount = this.getStepDiscount(step);
        const pristinePrice = Number(step.variant.price || 0) * quantity;
        const compareAtPrice =
          Math.max(Number(step.variant.price || 0), Number(step.variant.compare_at_price || 0)) * quantity;

        PurchaseModalPriceAdapter.apply(priceNormalize, {
          beforeDiscount: pristinePrice, referenceValue: compareAtPrice,
          offerDiscountPercentage: discount, showFrom: false, unitHeader: true,
        }, `purchase-modal-item:${step.variant.id}:${Date.now()}`);

        step.price.hidden = false;
        this.observeItemCouponTooltip(step.price);
      }

      observeItemCouponTooltip(container) {
        if (!container || this.itemPriceObservers.has(container)) return;

        const lifecycleId = this.lifecycleId;
        const enhance = () => {
          if (!this.destroyed && lifecycleId === this.lifecycleId) this.enhanceItemCouponTooltips(container);
        };

        const observer = new MutationObserver(enhance);

        observer.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.itemPriceObservers.set(container, observer);

        this.requestExperienceFrame(enhance);
      }

      enhanceItemCouponTooltips(container) {
        container
          .querySelectorAll("price-normalize.purchase-modal-item .coupon-code:not([data-pam-coupon-ready])")
          .forEach((coupon) => {
            const text = coupon.textContent?.trim();

            if (!text) return;

            coupon.dataset.pamCouponReady = "true";
            coupon.classList.add("pam-coupon-code");
            coupon.tabIndex = 0;
            coupon.setAttribute("role", "button");
            coupon.setAttribute("aria-expanded", "false");
            coupon.setAttribute("aria-label", `Cupom ${text}. Ative para exibir o código completo.`);

            const tooltip = document.createElement("span");

            tooltip.className = "pam-coupon-tooltip";
            tooltip.setAttribute("role", "tooltip");
            tooltip.textContent = text;

            const tooltipId = `PamCouponTooltip-${this.dataset.productId}-${Math.random().toString(36).slice(2)}`;

            tooltip.id = tooltipId;
            coupon.setAttribute("aria-controls", tooltipId);
            coupon.insertAdjacentElement("afterend", tooltip);

            const toggle = () => {
              const open = coupon.getAttribute("aria-expanded") !== "true";

              this.closeItemCouponTooltips(coupon);
              coupon.setAttribute("aria-expanded", String(open));
            };

            coupon.addEventListener("click", toggle, {
              signal: this.abortController.signal,
            });

            coupon.addEventListener(
              "keydown",
              (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggle();
                  return;
                }

                if (event.key === "Escape") {
                  coupon.setAttribute("aria-expanded", "false");
                }
              },
              { signal: this.abortController.signal },
            );
          });
      }

      closeItemCouponTooltips(except = null) {
        this.querySelectorAll(".pam-coupon-code[aria-expanded='true']").forEach((coupon) => {
          if (coupon === except) return;

          coupon.setAttribute("aria-expanded", "false");
        });
      }

      formatMoney(cents) {
        const roundedCents = PurchaseModalPricing.roundDisplayedPrice(Number(cents || 0));

        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: window.Shopify?.currency?.active || "BRL",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(roundedCents / 100);
      }

      getStepDiscount(step, config = this.config, rule = null) {
        return PurchaseModalPricing.stepDiscount(step, config, rule || this.selectedTier?.rule || config.rules?.[0] || {});
      }

      getPricingState() {
        return {
          experience: this.experience, config: this.config, selectedTier: this.selectedTier,
          compositionLxpy: this.isCompositionLxpy(),
          steps: this.steps.map(step => ({ product: step.product, variant: step.variant,
            quantity: step.quantity, selectedOptions: step.selectedOptions, composition: step.composition })),
          hasUserKitSelection: this.hasUserKitSelection,
          selectionComplete: this.isSelectionComplete(),
        };
      }

      updatePrice() {
        if (!this.priceNormalize) return;
        const result = PurchaseModalPricing.result(this.getPricingState(), { header: true });
        if (!result || (!result.unitHeader && !result.beforeDiscount)) {
          if (this.discountBadge) this.discountBadge.hidden = true;
          return;
        }
        PurchaseModalPriceAdapter.apply(this.priceNormalize, result, `purchase-modal:${this.experience}:${Date.now()}`);
        this.scheduleDiscountBadgeUpdate();
      }

      getBuyXPayYQuantity(config) {
        return PurchaseModalPricing.getBuyXPayYQuantity(config);
      }

      getBuyXPayYDisplayedDiscount(price) {
        return PurchaseModalPricing.getBuyXPayYDisplayedDiscount(price);
      }

      getBuyXPayYEffectiveDiscount(config) {
        return PurchaseModalPricing.getBuyXPayYEffectiveDiscount(config);
      }

      // preorder
      updateStepPreOrder(step) {
        if (!step.preorder) return;

        const allocation = step.variant?.selling_plan_allocations?.[0] || null;
        const isThemePreOrder = Boolean(allocation?.selling_plan_id) && allocation.app_id !== "timesact";

        step.sellingPlanAllocation = allocation;

        if (!isThemePreOrder || step.element.classList.contains("pam-step--plain")) {
          step.preorder.hidden = true;
          step.preorder.replaceChildren();
          return;
        }

        const { deliveryText } = this.getPreOrderDetails(allocation);

        step.preorder.hidden = false;
        step.preorder.innerHTML = `
          <strong class="pam-step__preorder-heading">Compra antecipada:</strong>
          <span class="pam-step__preorder-date">${this.escapeHtml(deliveryText)}</span>
        `;

        const howWorks = document.createElement("button");
        howWorks.type = "button";
        howWorks.className = "pam-step__preorder-how-works";
        howWorks.textContent = "Como funciona?";

        howWorks.addEventListener("click", () => window.BackorderDialogs?.openHowWorks?.(), {
          signal: this.abortController.signal,
        });

        step.preorder.append(howWorks);
      }

      getPreOrderDetails(allocation) {
        const name = String(allocation?.name || "");
        const description = String(allocation?.description || "").trim();

        const deadline = name.replace(/^Compra antecipada:\s*envio a partir de\s*/i, "").trim();

        return {
          deadline,
          deliveryText: description || deadline,
        };
      }

      getPreOrderDeliveryMessage(step) {
        const { deliveryText } = this.getPreOrderDetails(step?.sellingPlanAllocation);
        const product = step?.product;
        const variant = step?.variant;

        if (!product || !variant) return deliveryText || undefined;

        const details = product.options
          .map((option, index) => {
            const kind = this.optionKind(option.name);

            if (!["color", "size", "model"].includes(kind)) return null;
            if (!variant.options?.[index]) return null;

            return `${option.name}: ${variant.options[index]}`;
          })
          .filter(Boolean)
          .join(" | ");

        if (details && deliveryText) {
          return `• ${details} — ${deliveryText}`;
        }

        return deliveryText || details || undefined;
      }

      hasSinglePreOrder() {
        const step = this.steps[0];
        const allocation = step?.sellingPlanAllocation;
        return this.steps.length === 1 && Boolean(step?.element.classList.contains("pam-step--plain") &&
          step.variant && allocation?.selling_plan_id && allocation.app_id !== "timesact");
      }

      updateSubmitLabel() {
        if (!this.submitLabel) return;

        this.submitLabel.classList.remove("pam-product__submit-label--disabled");

        let delivery = this.submit?.querySelector("[data-pam-submit-delivery]");
        const step = this.steps[0];
        const allocation = step?.sellingPlanAllocation;
        const showDelivery = this.steps.length === 1 && step?.element.classList.contains("pam-step--plain") &&
          Boolean(step.variant && allocation?.selling_plan_id) && allocation.app_id !== "timesact";

        if (showDelivery && !delivery && this.submit) {
          delivery = document.createElement("span");
          delivery.dataset.pamSubmitDelivery = "";
          delivery.className = "pam-product__submit-delivery";
          this.submitLabel.insertAdjacentElement("afterend", delivery);
        }
        if (delivery) {
          const details = showDelivery ? this.getPreOrderDetails(allocation) : null;
          const date = (details?.deadline || details?.deliveryText || "")
            .replace(/^envio a partir de\s*/i, "").trim();
          const displayDate = date.replace(/^([A-ZÀ-Ú]{3})(?=\/)/, (month) => month[0] + month.slice(1).toLowerCase());
          delivery.textContent = date ? `Compra antecipada com envio a partir de ${displayDate}` : "";
          delivery.hidden = !date;
        }

        const singlePreOrder = this.hasSinglePreOrder();
        if (this.checkoutSubmit) this.checkoutSubmit.hidden = singlePreOrder;
        this.submit?.classList.toggle("pam-product__submit--preorder", singlePreOrder);
        if (singlePreOrder) {
          this.submitLabel.textContent = this.dataset.addLabel || "Adicionar ao carrinho";
          return;
        }

        if (this.experience !== "buy-x-pay-y") {
          this.submitLabel.textContent = this.dataset.addLabel;
          return;
        }

        const quantity = this.getSelectedBuyXPayYQuantity();

        this.submitLabel.textContent = `Adicionar ${quantity} ao carrinho`;
      }

      // cart
      syncSubmitTracking() {
        if (!this.submit) return;

        const product = this.products[0];

        this.submit.dataset.insKitId = this.config?.id ? String(this.config.id) : "";
        this.submit.dataset.insProductId = String(this.dataset.productId || product?.id || "");
        this.submit.dataset.insProductHandle = product?.handle || "";
        this.submit.dataset.insContent =
          this.querySelector(".pam-product__title")?.textContent?.trim() || product?.title || "";
        this.submit.dataset.insContext = this.dataset.requestedTrackingContext || "pdp";
      }

      hasValidCartQuantities(items) {
        return items.length > 0 && items.every(item => {
          if (!this.isValidQuantity(item.quantity)) return false;
          const variant = this.steps.find(step => String(step.variant?.id) === String(item.id))?.variant;
          if (!variant) return false;
          const rule = variant.quantity_rule || {};
          const min = Number(rule.min ?? 1);
          const increment = Number(rule.increment ?? 1);
          const max = rule.max == null ? Infinity : Number(rule.max);
          return this.isValidQuantity(min) && this.isValidQuantity(increment) &&
            item.quantity >= min && item.quantity <= max && (item.quantity - min) % increment === 0;
        });
      }

      resolveProductSource(config) {
        const requested = this.dataset.requestedProductSource;
        if (this.experience === "buy-together" && config.productSources?.includes("any")) {
          return requested || this.dataset.productSource || "buy-together";
        }
        if (requested && config.productSources?.includes(requested)) return requested;
        if (config.productSources?.includes(this.dataset.productSource)) return this.dataset.productSource;
        return config.productSources?.[0] || this.productSource || "pdk";
      }

      buildCartItems() {
        const grouped = new Map();
        const isSingleProduct = this.experience === "single-product";
        const isUnitPurchase = this.experience === "buy-x-pay-y" && this.selectedTier?.type === "unit";

        const config = isSingleProduct || isUnitPurchase ? null : this.selectedTier?.config || this.config;

        const productSource = config ? this.resolveProductSource(config) : null;
        const rule = config ? this.selectedTier?.rule || config.rules?.[0] || {} : {};

        this.steps.forEach((step) => {
          if (!step.variant) return;

          const quantity =
            this.experience === "progressive-discount"
              ? Number(rule.quantityRequiredForDiscount || 1)
              : this.experience === "buy-x-pay-y" && !this.isCompositionLxpy()
                ? this.getSelectedBuyXPayYQuantity()
                : Number(step.quantity || 1);
          const allocation = step.sellingPlanAllocation || step.variant.selling_plan_allocations?.[0] || null;
          const properties = {};

          if (!isUnitPurchase && config) {
            properties.__kitId = String(config.id);
            properties.__productSource = productSource;
          }
          if (allocation?.selling_plan_id && allocation.app_id !== "timesact") {
            const deadline = String(allocation.name || "")
              .replace(/^Compra antecipada:\s*envio a partir de\s*/i, "")
              .trim();
            if (deadline) properties["_Esse produto será despachado a partir de"] = deadline;
            properties.__backorder_app = String(allocation.app_id || "");
          }

          const groupingKey = `${step.variant.id}:${allocation?.selling_plan_id || ""}:${JSON.stringify(properties)}`;
          const existing = grouped.get(groupingKey);

          if (existing) {
            existing.quantity += quantity;
          } else {
            grouped.set(groupingKey, {
              id: step.variant.id,
              quantity,
              properties,
              ...(allocation?.selling_plan_id ? { selling_plan: allocation.selling_plan_id } : {}),
            });
          }
        });

        return [...grouped.values()];
      }

      isCurrentSubmission(operation) {
        return !this.destroyed && this.isConnected && this.operation === operation && this.lifecycleId === operation.lifecycleId;
      }

      releaseSubmission(operation) {
        if (!operation) return;
        operation.removeTermsListener?.();
        operation.removeTermsListener = null;
        operation.confirm = null;
        operation.cacheState = null;
        if (this.constructor.activeSubmission === operation) this.constructor.activeSubmission = null;
        if (this.operation === operation && this.lifecycleId === operation.lifecycleId && this.submissionState !== "succeeded") {
          this.submissionState = "idle";
        }
      }

      beginSubmission(items, options) {
        if (this.destroyed || !this.isConnected || this.submissionState === "succeeded" || this.constructor.activeSubmission) return null;
        const cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        const sections = cart?.getSectionsToRender
          ? cart.getSectionsToRender().map(section => section.id) : ["cart-drawer", "cart-icon-bubble"];
        // Snapshot antes de termos/await; nenhuma seleção posterior altera este POST.
        const body = JSON.stringify({ items, sections: sections.join(","), sections_url: window.location.pathname });
        const operationId = `purchase-modal-${(this.constructor.operationSequence || 0) + 1}`;
        this.constructor.operationSequence = (this.constructor.operationSequence || 0) + 1;
        const operation = {
          context: PurchaseModalIntegration.context(this, operationId, Boolean(options.redirectToCheckout)),
          facts: [], outcome: "pending", feedbackHandled: false,
          lifecycleId: this.lifecycleId, cacheState: this.cacheState,
          body, items: JSON.parse(body).items, redirectToCheckout: Boolean(options.redirectToCheckout),
          source: `purchase-modal:${this.experience}`, url: window.routes?.cart_add_url || "/cart/add.js", sent: false,
        };
        this.operation = operation;
        // Compartilhada apenas entre instâncias do Purchase Modal, inclusive reaberturas.
        this.constructor.activeSubmission = operation;
        this.submissionState = "awaiting-terms";
        return operation;
      }

      async submitOffer(options = {}) {
        if (this.destroyed || !this.isConnected || this.submissionState === "succeeded") return;
        if (this.constructor.activeSubmission) {
          if (this.constructor.activeSubmission !== this.operation) this.showError(this.dataset.requestErrorLabel);
          return;
        }
        if (!this.hasCompleteComposition()) {
          this.showError(this.dataset.missingConfigLabel);
          return;
        }
        if (!this.isSelectionComplete()) {
          this.handleIncompleteSelection();
          return;
        }
        const items = this.buildCartItems();
        if (!this.hasValidCartQuantities(items)) {
          this.showError(this.dataset.requestErrorLabel);
          return;
        }
        let operation;
        try {
          const singlePreOrder = this.hasSinglePreOrder();
          operation = this.beginSubmission(items, { ...options, redirectToCheckout: singlePreOrder ? false : options.redirectToCheckout });
          if (!operation) return;
          this.updateSubmitState();
          if (!singlePreOrder && items.some(item => item.selling_plan) && window.BackorderDialogs?.openPolicyTerms) {
            const preOrderStep = this.steps.find(step => step.sellingPlanAllocation?.selling_plan_id);
            const dialog = document.getElementById("backorder_dialog__policyterms");
            const onClose = () => {
              if (!operation.sent) {
                this.releaseSubmission(operation);
                if (this.isCurrentSubmission(operation)) this.updateSubmitState();
              }
            };
            dialog?.addEventListener("close", onClose, { once: true });
            operation.removeTermsListener = () => dialog?.removeEventListener("close", onClose);
            operation.confirm = () => {
              if (this.isCurrentSubmission(operation)) this.addItems(items, options, operation);
            };
            // O callback retido pelo dialog compartilhado não mantém a experiência após destroy.
            window.BackorderDialogs.openPolicyTerms(this.getPreOrderDeliveryMessage(preOrderStep), () => operation.confirm?.());
            if (!dialog?.open && !operation.sent) onClose();
            return;
          }
          await this.addItems(items, options, operation);
        } catch (error) {
          this.releaseSubmission(operation);
          if (this.submissionState === "succeeded") {
            console.error("Purchase modal: falha de apresentação após adição confirmada", error);
            return;
          }
          if (!this.destroyed && this.isConnected) {
            this.showError(error.message || this.dataset.requestErrorLabel);
            this.updateSubmitState();
          }
        }
      }

      async addItems(items, options = {}, operation = null) {
        if (!operation) {
          if (!this.isSelectionComplete() || !this.hasValidCartQuantities(items)) return;
          operation = this.beginSubmission(items, options);
        }
        if (!operation || operation.sent || this.constructor.activeSubmission !== operation || !this.isCurrentSubmission(operation)) return;
        operation.sent = true;
        operation.confirm = null;
        operation.removeTermsListener?.();
        operation.removeTermsListener = null;
        this.submissionState = "submitting";
        this.clearError();
        this.updateSubmitState();
        const submittedButton = operation.redirectToCheckout ? this.checkoutSubmit : this.submit;
        submittedButton?.classList.add("is-loading");

        let result;
        let rejectionStatus = null;
        try {
          PurchaseModalIntegration.record(operation, "attempt");
          // Sem signal visual: fechar o modal não garante cancelamento do POST.
          const response = await fetch(operation.url, {
            method: "POST",
            headers: {
              Accept: "application/json", "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest", "site-placement": operation.source,
            },
            body: operation.body,
          });
          result = await response.json();
          if (!response.ok || result.status) {
            rejectionStatus = Number(result.status || response.status) || null;
            // Status estruturado, independente de texto/tradução: estoque/variante/oferta podem ter mudado.
            if ([404, 409, 422].includes(Number(response.status)) || [404, 409, 422].includes(Number(result.status))) {
              operation.cacheState?.entries.clear();
              if (operation.cacheState) operation.cacheState.revision += 1;
            }
            throw new Error(result.description || result.message || this.dataset.requestErrorLabel);
          }
        } catch (error) {
          operation.outcome = rejectionStatus ? "rejected" : "unconfirmed";
          PurchaseModalIntegration.record(operation, "error", "cart", { outcome: operation.outcome, status: rejectionStatus });
          this.releaseSubmission(operation);
          if (this.isCurrentSubmission(operation)) {
            submittedButton?.classList.remove("is-loading");
            this.showError(error.message || this.dataset.requestErrorLabel);
            this.updateSubmitState();
          }
          return;
        }

        // Sucesso comercial é definitivo para esta experiência, antes de qualquer UI/pubsub.
        operation.outcome = "success";
        PurchaseModalIntegration.record(operation, "success");
        if (this.lifecycleId === operation.lifecycleId) this.submissionState = "succeeded";
        this.releaseSubmission(operation);
        if (!this.isCurrentSubmission(operation)) return;
        submittedButton?.classList.remove("is-loading");
        try {
          if (typeof window.publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
            await window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: operation.source, productVariantId: operation.items[0]?.id,
            });
          }
        } catch (error) {
          PurchaseModalIntegration.record(operation, "error", "notification");
          console.error("Purchase modal: falha na notificação após adição confirmada", error);
        }
        if (!this.isCurrentSubmission(operation)) return;
        try {
          this.dispatchEvent(new CustomEvent("purchase-modal:success", {
            bubbles: true,
            detail: { response: result, items: operation.items, redirectToCheckout: operation.redirectToCheckout },
          }));
        } catch (error) {
          PurchaseModalIntegration.record(operation, "error", "feedback");
          console.error("Purchase modal: falha no feedback após adição confirmada", error);
        }
      }
  });
}

if (!customElements.get("card-product-video")) {
  customElements.define(
    "card-product-video",
    class CardProductVideo extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.player = this.querySelector(".card-product-purchase__video-element");

        if (!this.player) return;

        this.player.muted = true;
        this.handleLoadedData = this.handleLoadedData.bind(this);

        if (this.player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          this.revealVideo();
        } else {
          this.player.addEventListener("loadeddata", this.handleLoadedData, {
            once: true,
          });
        }

        const playPromise = this.player.play?.();

        if (playPromise?.catch) {
          playPromise.catch(() => {
            // Autoplay pode ser bloqueado pelo browser. O poster permanece visível.
          });
        }
      }

      disconnectedCallback() {
        this.player?.removeEventListener("loadeddata", this.handleLoadedData);
        window.clearTimeout(this.revealTimeout);
        this.initialized = false;
      }

      handleLoadedData() {
        this.revealVideo();
      }

      revealVideo() {
        window.clearTimeout(this.revealTimeout);

        this.revealTimeout = window.setTimeout(() => {
          if (this.isConnected) {
            this.classList.add("is-loaded");
          }
        }, 150);
      }
    },
  );
}


}
